// The cost stack (ERP-PLAN §5.13): what one unit of a variant costs, layer by layer, planned (standard) and actual
// (one production run), shared by the screens (web/src/lib/costs.ts), the server (server/src/costs.ts: the MCP, the
// Shopify cost push, freezing a run's plan) and tests, so all of them agree to the cent.
//
// Layers, in waterfall order (shared/runs.ts LAYERS): materials, assembly & labour, packaging & packing, freight &
// tariffs, tooling & one-time, other. Planned:
// - Parts: the BOM resolved for the variant (shared/boms.ts resolve(), phantoms exploded), and stocked sub-assemblies
//   exploded too, so a PCBA's chips are materials and its PCBA service is assembly. Each part's price: the run's frozen
//   plan, else its standard cost, else its preferred vendor's USD quote at the quantity the run needs (price breaks).
// - The CM's fee: its build price for the item (a vendor link) when it has one, else the BOM's services plus the parts
//   the CM buys. Parts the CM buys stay in their layer at their planned price; assembly is the fee less them, so a
//   fee is never counted twice. Actual: the make PO line's price takes the fee's place, the same way.
// - Charges --- a build whose BOM has charges (build_charge, shared/charges.ts) is paid by them instead of a build
//   price: each in its own layer (per-order ones over the order's units), with its services and the parts the CM buys
//   as their own lines. Actual: a make PO line with charges splits its price by theirs (the parts the CM buys are in
//   them then: their "Parts the CM buys" row).
// - Freight: the project's rate (vendor_id null) as $ per finished unit or % of the unit's value, plus each vendor's
//   own rate for its goods (its value then leaves the default's % base). Duty: the variant's HTS code and country of
//   origin against duty_rate (layers add; in a layer the longest prefix wins), × the customs value: project
//   customs_basis 'full' (materials incl. the parts HHL supplied, which US customs counts as assists, + assembly +
//   packaging + tooling, molds being assists too) or 'cm_invoice' (only what the CM charges). Freight isn't in the
//   customs value.
// - Tooling: each tooling item's share (toolingStatus()), until it's paid off.
// Actual (runResult()): parts at what the run's POs paid (weighted by quantity; parts not bought for the run at the
// plan's price, flagged), at the quantity the CM consumed and scrapped per good unit; the make PO's fee; landed-cost
// charges on the run's POs (each charge counted once, in freight & tariffs, never in a part's price); the run's own
// cost entries (shared by value, a cost per unit by units: splitsByValue()); and the tooling shares by units actually built. Good units = built − rejected at receiving.
// A run's POs can carry lines that aren't its builds': another project's parts on the same vendor's PO count for
// nothing here (left out, with their landed-cost share), and lines marked run goods (po_line.run_goods: cables for
// backers) count in the run's spend but never in the cost per unit.
// Rounding: every layer to the cent with the total rounded once and the cents left over handed out by largest
// remainder (roundParts()), so layers always add up to the total shown.
import { type BomIndex, type BomItem, type BomRow, bomOf, byCm, lineApplies, type ResolvedCharge, resolve, type Supply, variantOptions } from "./boms.ts";
import { type ChargeLayer, layerShares } from "./charges.ts";
import type { MakeComponent } from "./consignment.ts";
import type {
  Attachment,
  Bill,
  BillLine,
  CmReport,
  CmReportLine,
  DutyRate,
  FreightRate,
  Item,
  LandedCost,
  LandedCostCharge,
  LandedCostReceipt,
  MakeOrder,
  Payment,
  Po,
  PoLine,
  PoLineCharge,
  PricePoint,
  ProductionCost,
  ProductionRun,
  ProductionRunLine,
  ProductionRunSupply,
  Project,
  Receipt,
  ReceiptLine,
  ToolingItem,
  Vendor,
  VendorItem,
} from "./db.gen.ts";
import { groupBy } from "./group.ts";
import { allocate, basisLines, receiptsInScope } from "./landed-cost.ts";
import { paidWithText } from "./pay-accounts.ts";
import { mulRound, priceFor, type VendorTerms } from "./po.ts";
import { marginFrom } from "./projects.ts";
import { CATEGORY_LAYER, COST_CATEGORY_LABELS, type CostCategory, LAYER_LABELS, LAYERS, type Layer, splitsByValue, TOOLING_KIND_LABELS } from "./runs.ts";

type Num = number | string | null | undefined;
const num = (v: Num) => (v == null || v === "" ? null : Number(v));
const usd = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: n !== 0 && Math.abs(n) < 1 ? 4 : 2 })}`;
const qtyText = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 3 });
const pctText = (n: number) => `${Number(n.toFixed(2))}%`;
const zero = (): Record<Layer, number> => ({ materials: 0, assembly: 0, packaging: 0, freight: 0, tooling: 0, other: 0 });
const day = (v: unknown) => (v == null ? null : String(v).slice(0, 10));

/** The price flag threshold: a PO line this much over its planned price is flagged ("RM2 +18% on PO-1042"). */
export const PRICE_FLAG_PCT = 5;

// Rounding ----------------------------------------------------------------------------------------------------

/**
 * Cents for each amount (USD) such that they add up to the total rounded once: each part's whole cents, then the cents
 * left over to the biggest remainders (the earlier one on a tie). Works for negative amounts (credits) too.
 */
export function roundParts(values: number[]): number[] {
  const scaled = values.map((v) => mulRound(v, 100, 6));
  const target = Math.round(
    mulRound(
      values.reduce((a, b) => a + b, 0),
      100,
      6,
    ),
  );
  const floors = scaled.map((v) => Math.floor(v + 1e-9));
  let left = target - floors.reduce((a, b) => a + b, 0);
  const order = scaled.map((v, i) => ({ i, frac: v - Math.floor(v + 1e-9) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  const out = [...floors];
  for (let k = 0; left > 0 && k < order.length; k++, left--) out[order[k].i] += 1;
  for (let k = order.length - 1; left < 0 && k >= 0; k--, left++) out[order[k].i] -= 1;
  return out;
}

/** Splits whole cents over weights (any sign of total; weights ≥ 0) exactly, by largest remainder. */
export function splitBy(totalCents: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + Math.max(0, b), 0);
  if (!weights.length) return [];
  if (!(sum > 0)) return weights.map((_, i) => (i === 0 ? totalCents : 0));
  return roundParts(weights.map((w) => (totalCents * Math.max(0, w)) / sum / 100));
}

const centsOf = (layers: Record<Layer, number>) => {
  const cents = roundParts(LAYERS.map((l) => layers[l]));
  return Object.fromEntries(LAYERS.map((l, i) => [l, cents[i]])) as Record<Layer, number>;
};
const sumCents = (c: Record<Layer, number>) => LAYERS.reduce((n, l) => n + c[l], 0);

// Data ------------------------------------------------------------------------------------------------------------

export type CostItem = Pick<
  Item,
  | "id"
  | "sku"
  | "name"
  | "kind"
  | "source"
  | "std_cost"
  | "price"
  | "hs_code"
  | "country_of_origin"
  | "shopify_product_id"
  | "shopify_variant_title"
  | "shopify_options"
  | "project_id"
  | "archived"
  | "weight_g"
  | "shopify_inventory_item_id"
  | "shopify_tracked"
>;

/** The synced rows the cost model reads (the browser passes its tables; server/src/costs.ts queries the same). */
export type CostData = {
  projects: readonly Pick<Project, "id" | "name" | "code" | "cm_vendor_id" | "customs_basis">[];
  items: readonly CostItem[];
  vendors: readonly Pick<Vendor, "id" | "name" | "short_name">[];
  vendorItems: readonly Pick<
    VendorItem,
    | "id"
    | "vendor_id"
    | "item_id"
    | "unit_price"
    | "currency"
    | "price_breaks"
    | "moq"
    | "order_multiple"
    | "vendor_sku"
    | "vendor_name"
    | "lead_time_days"
    | "is_preferred"
    | "quote_ref"
  >[];
  runs: readonly ProductionRun[];
  runLines: readonly ProductionRunLine[];
  pos: readonly Pick<
    Po,
    "id" | "number" | "vendor_id" | "kind" | "status" | "currency" | "production_run_id" | "created_at" | "dated_on" | "sent_on" | "vendor_ref"
  >[];
  poLines: readonly (Pick<
    PoLine,
    "id" | "po_id" | "position" | "item_id" | "sku" | "name" | "qty" | "unit_price" | "qty_received" | "qty_rejected" | "run_goods"
  > &
    // Pre-sales --- units of a make PO line that go straight from the CM to backers (shared/presales.ts)
    Partial<Pick<PoLine, "qty_direct" | "qty_direct_shipped">>)[];
  receipts: readonly Pick<Receipt, "id" | "po_id" | "number" | "received_at">[];
  receiptLines: readonly Pick<ReceiptLine, "id" | "receipt_id" | "po_line_id" | "item_id" | "qty_received" | "qty_rejected">[];
  landedCosts: readonly Pick<LandedCost, "id" | "number" | "scope" | "po_id" | "status" | "goods_rates" | "forwarder" | "invoice_ref" | "invoice_date">[];
  landedCostReceipts: readonly Pick<LandedCostReceipt, "landed_cost_id" | "receipt_id">[];
  landedCostCharges: readonly Pick<
    LandedCostCharge,
    "id" | "landed_cost_id" | "position" | "kind" | "description" | "amount" | "currency" | "usd_rate" | "method"
  >[];
  makeOrders: readonly Pick<MakeOrder, "id" | "po_id" | "po_line_id" | "item_id" | "bom_id" | "components" | "qty_output">[];
  cmReports: readonly Pick<CmReport, "id" | "kind" | "make_order_id" | "qty_output">[];
  cmReportLines: readonly Pick<CmReportLine, "report_id" | "item_id" | "kind" | "qty">[];
  costs: readonly ProductionCost[];
  tooling: readonly ToolingItem[];
  freightRates: readonly FreightRate[];
  dutyRates: readonly DutyRate[];
  pricePoints?: readonly PricePoint[];
  /** Which costs have their invoice (the run's "needs attention"). */
  attachments?: readonly Pick<Attachment, "owner_type" | "owner_id" | "kind">[];
  /** Bills and payments --- bills for the run's costs, tooling and POs: "Billed BL-1004" / "Paid" on Costs & invoices. */
  bills?: readonly Pick<Bill, "id" | "number" | "status" | "invoice_number">[];
  billLines?: readonly Pick<BillLine, "bill_id" | "po_id" | "production_cost_id" | "tooling_item_id">[];
  /** Payments whose fee may be one entered on the run by hand (`fee_review`): a person decides (Needs attention). */
  payments?: readonly Pick<Payment, "id" | "number" | "fee" | "fee_currency" | "fee_review" | "fee_matched_cost_id">[];
  /** Charges --- make PO lines' charges for their order (what the fee is made of, layer by layer). */
  poLineCharges?: readonly Pick<PoLineCharge, "po_line_id" | "position" | "name" | "amount" | "per" | "layer">[];
  /** Which card --- HHL's payment accounts, for "Paid Oct 16 by Amex ••1005 · the vendor's website" (migration 073). */
  payAccounts?: readonly { id: string; name: string; last4: string }[];
  /** Who supplies a part for one run, where the run says otherwise than its BOM (migration 057). */
  runSupply?: readonly Pick<ProductionRunSupply, "run_id" | "item_id" | "supply">[];
};

/** A bill a run's row is on (Bills and payments). */
export type RowBill = { id: string; number: string; status: string; invoice: string };

export type CostIndex = ReturnType<typeof indexCosts>;

/** Lookups for the cost model; build once per change of the rows (or of the BOM index). Results are cached on it. */
export function indexCosts(ix: BomIndex, d: CostData) {
  const runsByProject = groupBy(
    [...d.runs].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.number.localeCompare(b.number, undefined, { numeric: true })),
    (r) => r.project_id,
  );
  const lcPos = new Map<string, Set<string>>(); // landed cost → POs it covers
  const receiptById = new Map(d.receipts.map((r) => [r.id, r]));
  for (const lc of d.landedCosts) if (lc.scope === "po" && lc.po_id) lcPos.set(lc.id, new Set([lc.po_id]));
  for (const lr of d.landedCostReceipts) {
    const po = receiptById.get(lr.receipt_id)?.po_id;
    if (!po) continue;
    const set = lcPos.get(lr.landed_cost_id) ?? new Set<string>();
    set.add(po);
    lcPos.set(lr.landed_cost_id, set);
  }
  const lcsByPo = new Map<string, string[]>();
  for (const [lc, set] of lcPos) for (const po of set) lcsByPo.set(po, [...(lcsByPo.get(po) ?? []), lc]);
  return {
    ix,
    d,
    today: ix.today,
    itemById: new Map(d.items.map((i) => [i.id, i])),
    vendorById: new Map(d.vendors.map((v) => [v.id, v])),
    projectById: new Map(d.projects.map((p) => [p.id, p])),
    linksByItem: groupBy(
      [...d.vendorItems].sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred)),
      (l) => l.item_id,
    ),
    linkByVendorItem: new Map(d.vendorItems.map((l) => [`${l.vendor_id}|${l.item_id}`, l])),
    runById: new Map(d.runs.map((r) => [r.id, r])),
    runsByProject,
    /** Run → part → who supplies it for that run, where it says otherwise than the BOM (migration 057). */
    supplyByRun: (() => {
      const m = new Map<string, Map<string, Supply>>();
      for (const r of d.runSupply ?? []) {
        const byItem = m.get(r.run_id) ?? new Map<string, Supply>();
        byItem.set(r.item_id, r.supply);
        m.set(r.run_id, byItem);
      }
      return m;
    })(),
    linesByRun: groupBy(
      [...d.runLines].sort((a, b) => a.position - b.position),
      (l) => l.run_id,
    ),
    poById: new Map(d.pos.map((p) => [p.id, p])),
    posByRun: groupBy(
      d.pos.filter((p) => p.production_run_id),
      (p) => p.production_run_id as string,
    ),
    linesByPo: groupBy(
      [...d.poLines].sort((a, b) => a.position - b.position),
      (l) => l.po_id,
    ),
    poLineById: new Map(d.poLines.map((l) => [l.id, l])),
    chargesByLine: groupBy(
      [...(d.poLineCharges ?? [])].sort((a, b) => a.position - b.position),
      (c) => c.po_line_id,
    ),
    receiptsByPo: groupBy(d.receipts, (r) => r.po_id),
    lcById: new Map(d.landedCosts.map((l) => [l.id, l])),
    lcsByPo,
    lcReceipts: groupBy(d.landedCostReceipts, (r) => r.landed_cost_id),
    chargesByLc: groupBy(d.landedCostCharges, (c) => c.landed_cost_id),
    makeOrdersByPo: groupBy(d.makeOrders, (m) => m.po_id),
    reportsByMo: groupBy(
      d.cmReports.filter((r) => r.make_order_id),
      (r) => r.make_order_id as string,
    ),
    reportLinesByReport: groupBy(d.cmReportLines, (l) => l.report_id),
    costsByRun: groupBy(d.costs, (c) => c.run_id),
    toolingByProject: groupBy(d.tooling, (t) => t.project_id),
    freightByProject: groupBy(d.freightRates, (r) => r.project_id),
    pointsByItem: groupBy(
      [...(d.pricePoints ?? [])].sort((a, b) => String(a.at).localeCompare(String(b.at)) || a.created_at.localeCompare(b.created_at)),
      (p) => p.item_id,
    ),
    filesByOwner: groupBy(d.attachments ?? [], (a) => `${a.owner_type}:${a.owner_id}`),
    // Bills and payments --- the bills (not void) each cost, tooling item and PO is on.
    ...billsOf(d),
    cache: {
      tooling: new Map<string, ToolingStatus>(),
      result: new Map<string, RunResult | null>(),
      project: new Map<string, ProjectCosts | null>(),
      allocations: new Map<string, ReturnType<typeof allocate> | null>(),
      explode: new Map<string, Exploded>(),
    },
  };
}

function billsOf(d: CostData) {
  const bills = new Map((d.bills ?? []).filter((b) => b.status !== "void").map((b) => [b.id, b]));
  const byCost = new Map<string, RowBill[]>();
  const byTooling = new Map<string, RowBill[]>();
  const byPo = new Map<string, RowBill[]>();
  const put = (m: Map<string, RowBill[]>, key: string | null | undefined, billId: string) => {
    const b = bills.get(billId);
    if (!key || !b) return;
    const list = m.get(key) ?? [];
    if (!list.some((x) => x.id === b.id)) list.push({ id: b.id, number: b.number, status: b.status, invoice: b.invoice_number });
    m.set(key, list);
  };
  for (const l of d.billLines ?? []) {
    put(byCost, l.production_cost_id, l.bill_id);
    put(byTooling, l.tooling_item_id, l.bill_id);
    if (!l.production_cost_id && !l.tooling_item_id) put(byPo, l.po_id, l.bill_id);
  }
  return { billsByCost: byCost, billsByTooling: byTooling, billsByPo: byPo };
}

const vendorName = (cx: CostIndex, id: string | null | undefined) => {
  const v = id ? cx.vendorById.get(id) : undefined;
  return v ? v.short_name || v.name : "";
};

// Prices ------------------------------------------------------------------------------------------------------------

export type PriceSource = "frozen" | "std_cost" | "quote" | "rollup" | "run_po" | "missing";
export type Price = { unit: number | null; source: PriceSource; text: string; vendorId: string | null };

/** The plan's prices and rates, frozen when a run leaves planning (production_run.plan_basis). */
export type PlanBasis = {
  at: string;
  prices: Record<string, { unit: number | null; text: string; vendorId: string | null }>;
  fees: Record<string, { unit: number; text: string }>;
  /** Charges --- each charged build's charges per unit of it at the run's quantity (older plans have none: today's count). */
  charges?: Record<string, FrozenCharge[]>;
  freight: Pick<FreightRate, "vendor_id" | "basis" | "rate">[];
  duty: Record<string, { pct: number; text: string }>;
  customs_basis: "full" | "cm_invoice";
};
/** Charges --- one of a build's charges per unit of the build, as planned. */
export type FrozenCharge = { key: string; name: string; layer: ChargeLayer; unit: number | null; text: string };
export const basisOf = (run: Pick<ProductionRun, "plan_basis"> | undefined): PlanBasis | null => {
  const b = run?.plan_basis as Partial<PlanBasis> | null | undefined;
  return b?.at && b.prices ? (b as PlanBasis) : null;
};

const termsOf = (l: CostData["vendorItems"][number]): VendorTerms => ({
  vendor_sku: l.vendor_sku,
  vendor_name: l.vendor_name,
  unit_price: num(l.unit_price),
  currency: l.currency,
  price_breaks: l.price_breaks,
  moq: Number(l.moq ?? 0),
  order_multiple: Number(l.order_multiple ?? 1),
  lead_time_days: l.lead_time_days,
});

/** A part's planned unit price (USD) for `qty` units of it: frozen, standard cost, else the preferred USD quote. */
export function plannedPrice(cx: CostIndex, itemId: string, qty: number, basis?: PlanBasis | null, rollup?: number | null): Price {
  const frozen = basis?.prices[itemId];
  if (frozen) return { unit: frozen.unit, source: frozen.unit == null ? "missing" : "frozen", text: frozen.text, vendorId: frozen.vendorId };
  const item = cx.itemById.get(itemId);
  const links = cx.linksByItem.get(itemId) ?? [];
  const usdLink = links.find((l) => l.currency.toUpperCase() === "USD" && priceFor(termsOf(l), 1) != null);
  if (item?.std_cost != null)
    return { unit: Number(item.std_cost), source: "std_cost", text: `Standard cost ${usd(Number(item.std_cost))}`, vendorId: links[0]?.vendor_id ?? null };
  if (usdLink) {
    const terms = termsOf(usdLink);
    const at = Math.max(qty, terms.moq, 1);
    const unit = priceFor(terms, at) as number;
    const base = num(usdLink.unit_price);
    const broke = base != null && unit !== base;
    const v = vendorName(cx, usdLink.vendor_id) || "Vendor";
    return {
      unit,
      source: "quote",
      text: `${v} quote${usdLink.quote_ref ? ` ${usdLink.quote_ref}` : ""} ${usd(unit)}${broke ? ` (price break at ${qtyText(at)})` : ""}`,
      vendorId: usdLink.vendor_id,
    };
  }
  if (rollup != null) return { unit: rollup, source: "rollup", text: `BOM roll-up ${usd(rollup)}`, vendorId: null };
  const other = links.find((l) => priceFor(termsOf(l), 1) != null);
  return {
    unit: null,
    source: "missing",
    text: other
      ? `Only a ${other.currency} quote from ${vendorName(cx, other.vendor_id) || "a vendor"} (no USD rate)`
      : "No price: no standard cost or vendor quote",
    vendorId: other?.vendor_id ?? null,
  };
}

/** The CM's build price for an item (its vendor link), or null to use the BOM's services and CM-bought parts. */
export function plannedFee(
  cx: CostIndex,
  vendorId: string | null,
  itemId: string,
  qty: number,
  basis?: PlanBasis | null,
): { unit: number; text: string } | null {
  const frozen = basis?.fees[itemId];
  if (frozen) return frozen;
  if (basis) return null;
  const link = vendorId ? cx.linkByVendorItem.get(`${vendorId}|${itemId}`) : undefined;
  if (link?.currency.toUpperCase() !== "USD") return null;
  const unit = priceFor(termsOf(link), Math.max(qty, 1));
  return unit == null ? null : { unit, text: `${vendorName(cx, vendorId) || "The CM"}'s build price ${usd(unit)}` };
}

// Duty --------------------------------------------------------------------------------------------------------------

export type DutyLayer = { layer: string; pct: number; prefix: string; country: string; notes: string };

/**
 * Duty on an HTS code from a country on a day: rows in effect, by layer; in a layer the longest matching prefix wins,
 * a country's own row before an any-country one. Layers add up.
 */
export function dutyFor(rates: readonly DutyRate[], hts: string, country: string, on: string): { pct: number; layers: DutyLayer[] } {
  const code = (hts ?? "").replace(/\D/g, "");
  const coo = (country ?? "").toUpperCase();
  const best = new Map<string, DutyRate>();
  for (const r of rates) {
    if (r.hts_prefix && !code.startsWith(r.hts_prefix)) continue;
    if (r.country && r.country !== coo) continue;
    if (r.effective_from && day(r.effective_from)! > on) continue;
    if (r.effective_to && day(r.effective_to)! < on) continue;
    const had = best.get(r.layer.toLowerCase());
    const better =
      !had ||
      r.hts_prefix.length > had.hts_prefix.length ||
      (r.hts_prefix.length === had.hts_prefix.length && !!r.country && !had.country) ||
      (r.hts_prefix.length === had.hts_prefix.length && !!r.country === !!had.country && (day(r.effective_from) ?? "") > (day(had.effective_from) ?? ""));
    if (better) best.set(r.layer.toLowerCase(), r);
  }
  const layers = [...best.values()]
    .map((r) => ({ layer: r.layer, pct: Number(r.rate_pct), prefix: r.hts_prefix, country: r.country, notes: r.notes }))
    .sort((a, b) => a.layer.localeCompare(b.layer));
  return {
    pct: mulRound(
      layers.reduce((n, l) => n + l.pct, 0),
      1,
      4,
    ),
    layers,
  };
}

// The planned stack ------------------------------------------------------------------------------------------------

export type Piece = { label: string; usd: number; detail?: string; itemId?: string };
export type StackLine = {
  key: string;
  layer: Layer;
  itemId: string;
  sku: string;
  name: string;
  /** Per finished (good) unit, scrap included. */
  qty: number;
  unit: number | null;
  /** qty × unit, per unit; null when there's no price. */
  usd: number | null;
  supply: Supply;
  service: boolean;
  /** Who it comes from: the part's vendor, or the CM for what the CM buys. */
  vendorId: string | null;
  priceText: string;
  priceSource: PriceSource;
  /** The sub-assemblies it's in, by SKU ("GCU2-PCBA-R5"). */
  via: string;
  /** Inside the CM's fee (the CM buys it, or it's a service). */
  inFee: boolean;
  /** Production runs --- what the BOM says when the run says otherwise for this part (migration 057). */
  usual?: Supply;
  /** Charges --- a charge of the build's maker, not a part (no item: itemId and sku are ""). */
  charge?: boolean;
};
export type Stack = {
  kind: "planned" | "actual";
  variantId: string;
  sku: string;
  label: string;
  price: number | null;
  /** Per unit, USD, before rounding. */
  exact: Record<Layer, number>;
  /** Per unit, cents, adding up to totalCents. */
  cents: Record<Layer, number>;
  totalCents: number;
  total: number;
  lines: StackLine[];
  /** What each layer is made of, for the "why". */
  why: Record<Layer, Piece[]>;
  customs: { basis: "full" | "cm_invoice"; value: number; hts: string; country: string; pct: number; layers: DutyLayer[]; duty: number; frozen: boolean };
  /**
   * Pre-sales --- planned stacks: of the freight layer, the finished unit's own freight in (the project rate) and duty,
   * USD per unit; the rest of the layer is other vendors' goods' freight. knownUnitCost() keeps it until it's landed.
   */
  inbound?: number;
  /** Every part has a price and the BOM had nothing to warn about. */
  complete: boolean;
  missing: string[];
  flags: string[];
  margin: number | null;
};

export type WhatIf = { dutyPct?: number | null; freightRate?: number | null; part?: { itemId: string; unit: number } | null };

type Leaf = {
  key: string;
  itemId: string;
  item: BomItem;
  qty: number;
  supply: Supply;
  layer: Layer;
  service: boolean;
  /** The build (variant or stocked sub-assembly) whose CM's fee covers it if the CM buys it. */
  build: string;
  via: string[];
  rollup: number | null;
};
type BuildNode = { itemId: string; bom: BomRow; vendorId: string | null; qty: number; via: string[] };
/** Charges --- a charge of a build (its own or a phantom's in it), `mult` builds per finished unit, `via` the stocked path. */
type ChargeLeaf = { build: string; mult: number; c: ResolvedCharge; via: string[] };

const leafLayer = (item: { kind: string }, packaging: boolean): Layer => (packaging ? "packaging" : item.kind === "service" ? "assembly" : "materials");

/**
 * A variant's parts per finished unit, through phantom and stocked sub-assemblies, and the builds they're in. Cached.
 * Packaging --- `packaging`: it goes in on a packaging line (a stocked packaging kit), so all its parts are packaging.
 */
function explode(cx: CostIndex, bom: BomRow, variant: BomItem, vendorId: string | null, packaging = false): Exploded {
  const ck = `${bom.id}|${variant.id}|${vendorId ?? ""}${packaging ? "|packaging" : ""}`;
  const hit = cx.cache.explode.get(ck);
  if (hit) return hit;
  const out = explodeNow(cx, bom, variant, vendorId, packaging);
  cx.cache.explode.set(ck, out);
  return out;
}
type Exploded = { leaves: Leaf[]; builds: BuildNode[]; charges: ChargeLeaf[]; warnings: string[] };
function explodeNow(cx: CostIndex, bom: BomRow, variant: BomItem, vendorId: string | null, packaging: boolean): Exploded {
  const leaves = new Map<string, Leaf>();
  const builds: BuildNode[] = [{ itemId: variant.id, bom, vendorId, qty: 1, via: [] }];
  const charges: ChargeLeaf[] = [];
  const warnings: string[] = [];
  const options = variantOptions(variant);
  const walk = (b: BomRow, buildId: string, mult: number, via: string[], stack: Set<string>, packaging: boolean) => {
    const r = resolve(cx.ix, b, buildId === variant.id ? variant : cx.ix.itemById.get(buildId), { options });
    for (const w of r.warnings) warnings.push(via.length ? `${cx.ix.itemById.get(via.at(-1) as string)?.sku ?? "?"}: ${w}` : w);
    for (const c of r.charges) charges.push({ build: buildId, mult, c, via }); // Charges ---
    for (const l of r.lines) {
      if (l.phantom) continue;
      const pack = packaging || l.packaging;
      const sub = l.stocked && l.item.source === "local" ? bomOf(cx.ix, l.item) : undefined;
      if (sub && !stack.has(l.itemId)) {
        builds.push({ itemId: l.itemId, bom: sub, vendorId: sub.make_vendor_id, qty: mult * l.qty, via });
        walk(sub, l.itemId, mult * l.qty, [...via, l.itemId], new Set(stack).add(l.itemId), pack);
        continue;
      }
      const key = `${[...via, l.itemId].join("/")}|${l.supply}`;
      const had = leaves.get(key);
      if (had) had.qty += mult * l.qty;
      else
        leaves.set(key, {
          key,
          itemId: l.itemId,
          item: l.item,
          qty: mult * l.qty,
          supply: l.supply,
          layer: leafLayer(l.item, pack),
          service: l.item.kind === "service",
          build: buildId,
          via,
          rollup: l.stocked ? l.unitCost : null,
        });
    }
  };
  walk(bom, variant.id, 1, [], new Set([variant.id]), packaging);
  return { leaves: [...leaves.values()], builds, charges, warnings };
}

/**
 * Charges --- a build's charges per unit of it when `qty` are ordered: as the run's plan froze them, else as they are
 * (per-unit ones × how many of what they're on, per-order ones over the order).
 */
function chargesFor(
  cx: CostIndex,
  buildId: string,
  list: readonly ChargeLeaf[],
  qty: number,
  basis: PlanBasis | null,
  vendorId: string | null,
): FrozenCharge[] {
  const frozen = basis?.charges?.[buildId];
  if (frozen) return frozen;
  const who = vendorName(cx, vendorId) || "The maker";
  return list.map(({ c }) => {
    const under = c.via.length ? cx.itemById.get(c.via[c.via.length - 1])?.sku : "";
    const unit = c.amount == null ? null : c.per === "order" ? c.amount / Math.max(1, qty) : c.amount * c.qty;
    const text =
      c.amount == null
        ? `${who}'s charge: no amount yet`
        : c.per === "order"
          ? `${who}'s ${usd(c.amount)} an order, over ${qtyText(qty)}`
          : `${who}'s charge ${usd(c.amount)}${c.qty !== 1 ? ` × ${qtyText(c.qty)}` : ""}`;
    return { key: c.key, name: under ? `${c.name} (${under})` : c.name, layer: c.layer, unit, text };
  });
}

const viaText = (cx: CostIndex, via: string[]) => via.map((id) => cx.itemById.get(id)?.sku || "?").join(" › ");
/** "GCU2-SHELL × 1 @ $2.10", or a charge's name (× how many of what it's on, when not one). */
const stackLabel = (l: StackLine) =>
  l.charge ? `${l.name}${l.qty !== 1 ? ` × ${qtyText(l.qty)}` : ""}` : `${l.sku} × ${qtyText(l.qty)} @ ${usd(l.unit ?? 0)}`;

/** The BOM revision a run line builds from: its own, else the variant's current one. */
export function bomFor(cx: CostIndex, variant: BomItem, bomId?: string | null): BomRow | undefined {
  return (bomId ? cx.ix.bomById.get(bomId) : undefined) ?? bomOf(cx.ix, variant);
}

export type PlanOptions = {
  projectId: string;
  /** The run this is for: its frozen basis, its tooling share, its units for price breaks. */
  runId?: string | null;
  bomId?: string | null;
  /** Units of each part the run (or the planning volume) needs, for price breaks. */
  partQty?: ReadonlyMap<string, number>;
  /** This variant's units (the run's), for the tooling share. */
  units?: number;
  whatIf?: WhatIf;
  basis?: PlanBasis | null;
  /**
   * Packaging --- it goes in on a packaging line (a stocked packaging kit a run took from stock): all its parts are in
   * the packaging layer, as the plan of what it goes into puts them.
   */
  packaging?: boolean;
};

/** One variant's planned cost per unit, layer by layer, with what each number is made of. */
export function plannedStack(cx: CostIndex, variantId: string, o: PlanOptions): Stack | null {
  const variant = cx.ix.itemById.get(variantId);
  const item = cx.itemById.get(variantId);
  if (!variant || !item) return null;
  const bom = bomFor(cx, variant, o.bomId);
  const project = cx.projectById.get(o.projectId);
  const run = o.runId ? cx.runById.get(o.runId) : undefined;
  const basis = o.basis !== undefined ? o.basis : basisOf(run);
  const cmId = run?.cm_vendor_id ?? bom?.make_vendor_id ?? project?.cm_vendor_id ?? null;
  const exact = zero();
  const why: Record<Layer, Piece[]> = { materials: [], assembly: [], packaging: [], freight: [], tooling: [], other: [] };
  const lines: StackLine[] = [];
  const missing: string[] = [];
  const flags: string[] = [];
  const label = item.shopify_variant_title || item.name;
  if (!bom) {
    return emptyStack("planned", item, label, [`${item.sku} has no BOM, so its cost can't be planned`]);
  }
  const { leaves, builds, charges, warnings } = explode(cx, bom, variant, cmId, !!o.packaging);
  for (const w of warnings) missing.push(w);
  // Production runs --- who supplies a part is the run's word where it has one, else its BOM line's (migration 057).
  const runSupply = o.runId ? cx.supplyByRun.get(o.runId) : undefined;
  const supplyOf = (leaf: Leaf): Supply => runSupply?.get(leaf.itemId) ?? leaf.supply;
  const buildVendor = new Map(builds.map((b) => [b.itemId, b.itemId === variantId ? cmId : b.vendorId]));
  const goods = new Map<string, number>(); // vendor ("" unknown) → value of its goods in one unit

  const price = (leaf: Leaf) => {
    const p = plannedPrice(cx, leaf.itemId, (o.partQty?.get(leaf.itemId) ?? 0) || Math.max(1, leaf.qty), basis, leaf.rollup);
    if (o.whatIf?.part && o.whatIf.part.itemId === leaf.itemId)
      return { ...p, unit: o.whatIf.part.unit, source: "quote" as const, text: `What if: ${usd(o.whatIf.part.unit)} (${p.text})` };
    return p;
  };
  // Each build's CM fee: its build price, else its services and CM-bought parts (those already are their own lines).
  const feeParts = new Map<string, number>(); // build → planned CM-bought parts (not services) per unit of the build
  const services = new Map<string, number>();
  for (const leaf of leaves) {
    const p = price(leaf);
    const ext = p.unit == null ? null : leaf.qty * p.unit;
    const supply = supplyOf(leaf);
    const inFee = byCm(supply) || leaf.service;
    const vendorId = inFee ? (buildVendor.get(leaf.build) ?? null) : p.vendorId;
    if (ext == null) missing.push(`${leaf.item.sku || leaf.item.name}${leaf.via.length ? ` (in ${viaText(cx, leaf.via)})` : ""}: ${p.text}`);
    const b = builds.find((x) => x.itemId === leaf.build);
    const perBuild = b && b.qty > 0 ? (ext ?? 0) / b.qty : 0;
    if (inFee && !leaf.service) feeParts.set(leaf.build, (feeParts.get(leaf.build) ?? 0) + perBuild);
    if (leaf.service) services.set(leaf.build, (services.get(leaf.build) ?? 0) + perBuild);
    lines.push({
      key: leaf.key,
      layer: leaf.layer,
      itemId: leaf.itemId,
      sku: leaf.item.sku,
      name: leaf.item.name,
      qty: leaf.qty,
      unit: p.unit,
      usd: ext,
      supply,
      service: leaf.service,
      vendorId,
      priceText: p.text,
      priceSource: p.source,
      via: viaText(cx, leaf.via),
      inFee,
      ...(supply !== leaf.supply ? { usual: leaf.supply } : {}),
    });
  }
  // Assembly: per build, its fee less the CM-bought parts already counted (the services' own lines are replaced).
  for (const b of builds) {
    const vendor = buildVendor.get(b.itemId) ?? null;
    const ordered = (o.partQty?.get(b.itemId) ?? 0) || Math.max(1, b.qty * (o.units ?? 1));
    const who = b.itemId === variantId ? item.sku : cx.itemById.get(b.itemId)?.sku;
    // Charges --- a build with charges is paid by them, each a line in its layer; a build price doesn't count then.
    const own = charges.filter((x) => x.build === b.itemId);
    if (own.length) {
      for (const ch of chargesFor(cx, b.itemId, own, ordered, basis, vendor)) {
        if (ch.unit == null) missing.push(`${who}: its charge ${ch.name} has no amount yet`);
        lines.push({
          key: `charge:${b.itemId}:${ch.key}`,
          layer: ch.layer,
          itemId: "",
          sku: "",
          name: ch.name,
          qty: b.qty,
          unit: ch.unit,
          usd: ch.unit == null ? null : ch.unit * b.qty,
          supply: "cm_buys",
          service: false,
          charge: true,
          vendorId: vendor,
          priceText: ch.text,
          priceSource: basis?.charges?.[b.itemId] ? "frozen" : "quote",
          via: viaText(cx, b.itemId === variantId ? [] : [...b.via, b.itemId]),
          inFee: true,
        });
      }
      continue;
    }
    const fee = plannedFee(cx, vendor, b.itemId, ordered, basis);
    const parts = feeParts.get(b.itemId) ?? 0;
    if (fee) {
      // The build price replaces the services' own prices; parts the CM buys stay in their layers.
      for (const l of lines) if (l.service && leaves.find((x) => x.key === l.key)?.build === b.itemId) l.usd = 0;
      const assy = (fee.unit - parts) * b.qty;
      exact.assembly += assy;
      why.assembly.push({
        label: `${who}: ${fee.text}${parts ? ` less the parts ${vendorName(cx, vendor) || "the CM"} buys (${usd(parts)})` : ""}${b.qty !== 1 ? ` × ${qtyText(b.qty)}` : ""}`,
        usd: assy,
      });
      goods.set(vendor ?? "", (goods.get(vendor ?? "") ?? 0) + assy);
    }
  }
  for (const l of lines) {
    if (l.usd == null) continue;
    exact[l.layer] += l.usd;
    if (!(l.service && l.usd === 0)) goods.set(l.vendorId ?? "", (goods.get(l.vendorId ?? "") ?? 0) + l.usd);
  }
  for (const layer of ["materials", "packaging"] as const)
    why[layer] = lines
      .filter((l) => l.layer === layer && l.usd != null)
      .map((l) => ({ label: stackLabel(l), usd: l.usd as number, detail: l.priceText, ...(l.itemId ? { itemId: l.itemId } : {}) }));
  for (const l of lines) if (l.layer === "assembly" && l.usd) why.assembly.push({ label: stackLabel(l), usd: l.usd, detail: l.priceText });
  // Charges --- one in the tooling layer (a stencil per order) is said there, with the tooling shares below.
  for (const l of lines) if (l.layer === "tooling" && l.usd) why.tooling.push({ label: stackLabel(l), usd: l.usd, detail: l.priceText });

  // Freight: the project's rate plus each vendor's own.
  const value = exact.materials + exact.assembly + exact.packaging;
  const rates = basis?.freight ?? (cx.freightByProject.get(o.projectId) ?? []).map((r) => ({ vendor_id: r.vendor_id, basis: r.basis, rate: Number(r.rate) }));
  const own = rates.filter((r) => r.vendor_id && goods.has(r.vendor_id));
  let ownValue = 0;
  for (const r of own) {
    const g = goods.get(r.vendor_id as string) ?? 0;
    ownValue += g;
    const f = r.basis === "per_unit" ? Number(r.rate) : (Number(r.rate) / 100) * g;
    exact.freight += f;
    why.freight.push({
      label: `Freight for ${vendorName(cx, r.vendor_id)}'s goods: ${r.basis === "per_unit" ? `${usd(Number(r.rate))} a unit` : `${pctText(Number(r.rate))} of ${usd(g)}`}`,
      usd: f,
      detail: "The vendor's own rate (Project → Costs → Planned rates)",
    });
  }
  const def = rates.find((r) => !r.vendor_id);
  const defRate = o.whatIf?.freightRate != null ? o.whatIf.freightRate : def ? Number(def.rate) : null;
  const defBasis = def?.basis ?? "per_unit";
  // Pre-sales --- the finished unit's own freight in (the project rate) and its duty, below.
  let inbound = 0;
  if (defRate != null) {
    const base = value - ownValue;
    const f = defBasis === "per_unit" ? defRate : (defRate / 100) * base;
    exact.freight += f;
    inbound += f;
    why.freight.push({
      label: `Freight, project rate: ${defBasis === "per_unit" ? `${usd(defRate)} a unit` : `${pctText(defRate)} of ${usd(base)}${own.length ? " (goods without their own rate)" : ""}`}`,
      usd: f,
      detail: o.whatIf?.freightRate != null ? "What if" : basis ? `Frozen ${basis.at}` : "Project → Costs → Planned rates",
    });
  } else if (!own.length) flags.push("No planned freight rate: set one on Project → Costs → Planned rates");

  // Tooling: each item's share.
  for (const p of toolingPieces(cx, o.projectId, item, o.runId ?? null, o.units)) {
    exact.tooling += p.usd;
    why.tooling.push(p);
  }

  // Duty on the customs value: on the full basis the molds HHL paid for count too (assists, like its parts).
  const customsBasis = basis?.customs_basis ?? project?.customs_basis ?? "full";
  const cmGoods = cmId ? (goods.get(cmId) ?? 0) : 0;
  const customsValue = customsBasis === "full" ? value + exact.tooling : cmGoods;
  const frozenDuty = basis?.duty[variantId];
  const looked = dutyFor(cx.d.dutyRates, item.hs_code, item.country_of_origin, day(run?.target_ship) ?? cx.today);
  const dutyPct = o.whatIf?.dutyPct != null ? o.whatIf.dutyPct : frozenDuty ? frozenDuty.pct : looked.pct;
  const duty = (dutyPct / 100) * customsValue;
  if (!item.hs_code) flags.push(`${item.sku} has no HTS code, so no duty is planned`);
  else if (!item.country_of_origin) flags.push(`${item.sku} has no country of origin, so only any-country duty rates apply`);
  exact.freight += duty;
  inbound += duty;
  why.freight.push({
    label: `Duty: ${pctText(dutyPct)} of ${usd(customsValue)} customs value${customsBasis === "full" && exact.tooling ? ` (tooling's ${usd(exact.tooling)} included)` : ""}`,
    usd: duty,
    detail:
      o.whatIf?.dutyPct != null
        ? "What if"
        : frozenDuty
          ? `Frozen ${basis?.at}: ${frozenDuty.text}`
          : looked.layers.length
            ? `HTS ${item.hs_code || "—"} from ${item.country_of_origin || "any country"}: ${looked.layers.map((l) => `${l.layer} ${pctText(l.pct)}`).join(" + ")}`
            : `No duty rate matches HTS ${item.hs_code || "—"} from ${item.country_of_origin || "—"}`,
  });

  return finish("planned", item, label, exact, why, lines, {
    customs: {
      basis: customsBasis,
      value: customsValue,
      hts: item.hs_code,
      country: item.country_of_origin,
      pct: dutyPct,
      layers: frozenDuty ? [] : looked.layers,
      duty,
      frozen: !!frozenDuty,
    },
    missing,
    flags,
    inbound,
  });
}

function emptyStack(kind: Stack["kind"], item: CostItem, label: string, missing: string[]): Stack {
  return finish(kind, item, label, zero(), { materials: [], assembly: [], packaging: [], freight: [], tooling: [], other: [] }, [], {
    customs: { basis: "full", value: 0, hts: item.hs_code, country: item.country_of_origin, pct: 0, layers: [], duty: 0, frozen: false },
    missing,
    flags: [],
  });
}

function finish(
  kind: Stack["kind"],
  item: CostItem,
  label: string,
  exact: Record<Layer, number>,
  why: Record<Layer, Piece[]>,
  lines: StackLine[],
  extra: Pick<Stack, "customs" | "missing" | "flags"> & Partial<Pick<Stack, "inbound">>,
): Stack {
  const cents = centsOf(exact);
  const totalCents = sumCents(cents);
  const price = num(item.price);
  const complete = !extra.missing.length;
  return {
    kind,
    variantId: item.id,
    sku: item.sku,
    label,
    price,
    exact,
    cents,
    totalCents,
    total: totalCents / 100,
    lines,
    why,
    ...extra,
    complete,
    margin: complete ? marginFrom(price, totalCents / 100) : null,
  };
}

// Tooling -------------------------------------------------------------------------------------------------------

/**
 * One run's share of a tooling item: by its good units once built (planned before), from `from` (the units of the runs
 * before it) to `to`; plannedCents is what its planned units would carry after the same earlier runs.
 */
export type ToolingRunShare = {
  runId: string;
  number: string;
  units: number;
  built: boolean;
  cents: number;
  from: number;
  to: number;
  plannedUnits: number;
  plannedCents: number;
};
export type ToolingStatus = {
  tooling: ToolingItem;
  usd: number;
  cents: number;
  spread: number;
  /** Units built so far, of the variants it applies to, in the runs it counts (from the run that paid for it). */
  built: number;
  /** Built plus what open runs plan to build. */
  planned: number;
  paidOff: boolean;
  /** Its share per unit while it isn't paid off. */
  perUnit: number;
  runs: ToolingRunShare[];
  appliesTo: string[];
  text: string;
};

export const toolingUsd = (t: Pick<ToolingItem, "amount" | "currency" | "usd_rate">) =>
  t.currency.toUpperCase() === "USD" ? mulRound(Number(t.amount), 1, 2) : mulRound(Number(t.amount), Number(t.usd_rate), 2);

/** Whether a tooling item counts for a variant (its applies_when, the BOM condition shape). */
export const toolingApplies = (t: Pick<ToolingItem, "applies_when">, item: Pick<Item, "shopify_options">) =>
  lineApplies({ applies_when: t.applies_when } as Parameters<typeof lineApplies>[0], variantOptions(item));

/**
 * A tooling item's payoff: the project's runs from the one that paid for it, each with its units of the variants it
 * applies to (built, else planned while the run is open) and its share in cents. Shares come from the cumulative
 * units (amount × min(to, spread) ÷ spread, rounded, less the same before), so they add up to the amount exactly and
 * nothing more is charged once the spread is reached. spread_units 0: all of it on the run that paid for it.
 */
export function toolingStatus(cx: CostIndex, t: ToolingItem): ToolingStatus {
  const hit = cx.cache.tooling.get(t.id);
  if (hit) return hit;
  const amount = toolingUsd(t);
  const cents = Math.round(mulRound(amount, 100, 6));
  const spread = Number(t.spread_units);
  const runs = cx.runsByProject.get(t.project_id) ?? [];
  const from = t.paid_by_run_id ? runs.findIndex((r) => r.id === t.paid_by_run_id) : 0;
  const counted = from < 0 ? [] : runs.slice(from);
  const variantIds = new Set<string>();
  const payer = t.paid_by_run_id ?? counted[0]?.id;
  const shareOf = (from: number, units: number, runId: string) =>
    spread > 0
      ? Math.round((cents * Math.min(from + units, spread)) / spread) - Math.round((cents * Math.min(from, spread)) / spread)
      : runId === payer
        ? cents
        : 0;
  const shares: ToolingRunShare[] = [];
  let cum = 0;
  let built = 0;
  for (const r of counted) {
    let units = 0;
    let plannedUnits = 0;
    let isBuilt = false;
    for (const [id, u] of runUnits(cx, r.id)) {
      const item = cx.itemById.get(id);
      // The project's products only: a sub-assembly built on the run is inside them, not a unit of its own.
      if (item?.kind !== "sell" || !toolingApplies(t, item)) continue;
      variantIds.add(id);
      plannedUnits += u.planned;
      if (u.built > 0) {
        units += u.good;
        isBuilt = true;
      } else if (r.status !== "closed") units += u.planned;
    }
    if (isBuilt) built += units;
    shares.push({
      runId: r.id,
      number: r.number,
      units,
      built: isBuilt,
      cents: shareOf(cum, units, r.id),
      from: cum,
      to: cum + units,
      plannedUnits,
      plannedCents: shareOf(cum, plannedUnits, r.id),
    });
    cum += units;
  }
  const paidOff = spread > 0 ? built >= spread : built > 0;
  const perUnit = spread > 0 ? amount / spread : 0;
  const text =
    spread > 0
      ? `${usd(amount)} over ${qtyText(spread)} units = ${usd(perUnit)} a unit · ${qtyText(Math.min(built, spread))} of ${qtyText(spread)} built${paidOff ? ": paid off" : ""}`
      : `${usd(amount)}, all on ${cx.runById.get(t.paid_by_run_id ?? "")?.number ?? "the run that paid for it"}`;
  const status: ToolingStatus = {
    tooling: t,
    usd: amount,
    cents,
    spread,
    built,
    planned: cum,
    paidOff,
    perUnit,
    runs: shares,
    appliesTo: [...variantIds],
    text,
  };
  cx.cache.tooling.set(t.id, status);
  return status;
}

/** The tooling pieces of one unit of a variant: in a run, its share of that run; else the rate while not paid off. */
function toolingPieces(cx: CostIndex, projectId: string, item: CostItem, runId: string | null, _units?: number): Piece[] {
  const out: Piece[] = [];
  for (const t of cx.toolingByProject.get(projectId) ?? []) {
    if (t.retired || !toolingApplies(t, item)) continue;
    const st = toolingStatus(cx, t);
    const name = `${TOOLING_KIND_LABELS[t.kind]}: ${t.name}`;
    if (runId) {
      const share = st.runs.find((r) => r.runId === runId);
      if (!share?.plannedUnits || !share.plannedCents) continue;
      const per = share.plannedCents / 100 / share.plannedUnits;
      out.push({
        label: name,
        usd: per,
        detail: `${usd(share.plannedCents / 100)} of ${usd(st.usd)} on this run's ${qtyText(share.plannedUnits)} planned units (units ${qtyText(share.from + 1)}–${qtyText(share.from + share.plannedUnits)}${st.spread ? ` of ${qtyText(st.spread)}` : ""})`,
      });
    } else if (st.paidOff) out.push({ label: name, usd: 0, detail: `Paid off: ${st.text}` });
    else if (st.spread > 0) out.push({ label: name, usd: st.perUnit, detail: st.text });
  }
  return out;
}

// Runs: units, prices, actuals ------------------------------------------------------------------------------------

export type RunUnits = {
  planned: number;
  ordered: number;
  built: number;
  /** Finished units scrapped at the CM. */
  scrapped: number;
  shipped: number;
  received: number;
  rejected: number;
  /**
   * What actual costs are divided by: built − rejected − scrapped; the plan while nothing is built on a run still going;
   * 0 for a variant a closed run never built.
   */
  good: number;
  /** Nothing built yet on a run still going: good is the planned units, assumed. */
  estimate: boolean;
  /** The run closed without building any: no good units, no actual cost, none of the run's charges. */
  notBuilt: boolean;
  /**
   * Set aside --- of a past run's units built, those that weren't HHL's to sell (kept by the CM for its own store:
   * migration 069). Not good units: their cost is carried by the units kept.
   */
  setAside: number;
};

const OPEN_POS = (status: string) => status !== "cancelled";

/** Each item the run builds (its plan lines and its make orders) with planned, built, received and good units. */
export function runUnits(cx: CostIndex, runId: string): Map<string, RunUnits> {
  const out = new Map<string, RunUnits>();
  const get = (id: string) => {
    let u = out.get(id);
    if (!u)
      out.set(
        id,
        (u = { planned: 0, ordered: 0, built: 0, scrapped: 0, shipped: 0, received: 0, rejected: 0, good: 0, estimate: true, notBuilt: false, setAside: 0 }),
      );
    return u;
  };
  for (const l of cx.linesByRun.get(runId) ?? []) get(l.item_id).planned += Number(l.qty);
  const run = cx.runById.get(runId);
  const opening = (run?.opening as { lines?: { make_order_id: string; shipped?: number }[] } | null)?.lines ?? [];
  for (const po of cx.posByRun.get(runId) ?? []) {
    if (po.kind !== "make" || !OPEN_POS(po.status)) continue;
    for (const mo of cx.makeOrdersByPo.get(po.id) ?? []) {
      const u = get(mo.item_id);
      const line = cx.poLineById.get(mo.po_line_id);
      u.ordered += Number(line?.qty ?? 0);
      u.built += Number(mo.qty_output);
      u.received += Number(line?.qty_received ?? 0);
      u.rejected += Number(line?.qty_rejected ?? 0);
      u.shipped += Math.max(
        Number(opening.find((o) => o.make_order_id === mo.id)?.shipped ?? 0),
        Number(line?.qty_received ?? 0) + Number(line?.qty_rejected ?? 0),
      );
      for (const r of cx.reportsByMo.get(mo.id) ?? [])
        for (const rl of cx.reportLinesByReport.get(r.id) ?? []) if (rl.kind === "scrap" && rl.item_id === mo.item_id) u.scrapped += Number(rl.qty);
    }
  }
  // A past run (migration 057): its plan lines are the units it built, all good and delivered but those set aside.
  if (run?.historical) {
    const aside = new Map((cx.linesByRun.get(runId) ?? []).map((l) => [l.item_id, Number(l.set_aside ?? 0)]));
    for (const [id, u] of out) {
      u.built = u.planned;
      u.setAside = Math.min(u.planned, aside.get(id) ?? 0);
      u.received = u.shipped = u.good = u.planned - u.setAside;
      u.rejected = u.scrapped = 0;
      u.estimate = false;
      u.notBuilt = u.planned <= 0;
    }
    return out;
  }
  // Nothing built: on a run still going the plan stands in; a closed run won't build it, so it has no good units (its
  // planned ones would take a share of the run's charges from the variants that were built).
  const closed = run?.status === "closed";
  for (const u of out.values()) {
    u.estimate = u.built <= 0 && !closed;
    u.notBuilt = u.built <= 0 && closed;
    u.good = u.built > 0 ? Math.max(0, u.built - u.rejected - u.scrapped) : closed ? 0 : u.planned || u.ordered;
  }
  return out;
}

/** The PO's USD rate for its currency (1 for USD), from a landed cost's goods rates; null when there's none. */
function poRate(cx: CostIndex, po: CostData["pos"][number]): number | null {
  if (po.currency.toUpperCase() === "USD") return 1;
  for (const lc of cx.lcsByPo.get(po.id) ?? []) {
    const r = Number((cx.lcById.get(lc)?.goods_rates as Record<string, number> | null)?.[po.currency.toUpperCase()]);
    if (r > 0) return r;
  }
  return null;
}

/** "Paid Oct 16 by Amex ••1005 · The vendor's website · 317104" for a run cost or tooling item paid outside (Which card ---). */
function paidText(cx: CostIndex, r: { paid_on: string | null; paid_method: string; paid_ref: string; paid_account_id?: string | null; paid_via?: string }) {
  if (!r.paid_on) return "";
  const account = r.paid_account_id ? cx.d.payAccounts?.find((a) => a.id === r.paid_account_id) : null;
  const how = paidWithText({ account, via: r.paid_via, method: r.paid_method });
  return `Paid ${day(r.paid_on)}${how ? ` by ${how}` : ""}${r.paid_ref ? ` · ${r.paid_ref}` : ""}`;
}

/** What the run's (non-make) POs paid for an item: the quantity-weighted USD price and the lines. */
function runPoPrice(cx: CostIndex, runId: string, itemId: string) {
  let qty = 0;
  let total = 0;
  const lines: { po: string; qty: number; unit: number }[] = [];
  let noRate = "";
  for (const po of cx.posByRun.get(runId) ?? []) {
    if (po.kind === "make" || !OPEN_POS(po.status)) continue;
    const rate = poRate(cx, po);
    for (const l of cx.linesByPo.get(po.id) ?? []) {
      if (l.item_id !== itemId || l.unit_price == null || l.run_goods) continue;
      if (rate == null) {
        noRate = `${po.number} is in ${po.currency} with no USD rate yet`;
        continue;
      }
      const q = Number(l.qty_received) > 0 ? Number(l.qty_received) : Number(l.qty);
      const unit = mulRound(Number(l.unit_price), rate, 6);
      qty += q;
      total += q * unit;
      lines.push({ po: po.number, qty: q, unit });
    }
  }
  return { unit: qty > 0 ? total / qty : null, qty, lines, noRate };
}

/**
 * Pre-sales --- what the run's (non-make) POs paid for an item, run goods included (add-ons bought for backers): the
 * quantity-weighted USD price and the lines. unit null when none of them has a USD price (or a rate for one).
 */
export function runBoughtPrice(cx: CostIndex, runId: string, itemId: string) {
  let qty = 0;
  let total = 0;
  const lines: { po: string; qty: number; unit: number }[] = [];
  for (const po of cx.posByRun.get(runId) ?? []) {
    if (po.kind === "make" || !OPEN_POS(po.status)) continue;
    const rate = poRate(cx, po);
    if (rate == null) continue;
    for (const l of cx.linesByPo.get(po.id) ?? []) {
      if (l.item_id !== itemId || l.unit_price == null) continue;
      const q = Number(l.qty);
      const unit = mulRound(Number(l.unit_price), rate, 6);
      qty += q;
      total += q * unit;
      lines.push({ po: po.number, qty: q, unit });
    }
  }
  return { unit: qty > 0 ? total / qty : null, qty, lines };
}

/** A make order's consumption per component (consumed + scrapped), from its reports. */
function consumption(cx: CostIndex, moIds: string[]): Map<string, { consumed: number; scrapped: number }> {
  const out = new Map<string, { consumed: number; scrapped: number }>();
  for (const id of moIds)
    for (const r of cx.reportsByMo.get(id) ?? [])
      for (const rl of cx.reportLinesByReport.get(r.id) ?? []) {
        if (rl.kind !== "consume" && rl.kind !== "scrap") continue;
        const c = out.get(rl.item_id) ?? { consumed: 0, scrapped: 0 };
        if (rl.kind === "consume") c.consumed += Number(rl.qty);
        else c.scrapped += Number(rl.qty);
        out.set(rl.item_id, c);
      }
  return out;
}

export type ActualLeaf = StackLine & { plannedUnit: number | null; plannedQty: number; tracked: boolean };

export type RunRowKind = "po_line" | "make_fee" | "landed" | "manual" | "tooling";
/**
 * What a row counts toward: "unit" the cost per unit (and the run's spend), "run_goods" only the run's spend (a PO line
 * marked run goods, and its landed-cost share), "left_out" nothing (a line on the run's PO the run doesn't build with),
 * "presale" the run's spend and its pre-sale's result, never the cost per unit (shipping to backers: backer_shipping).
 */
export type RunRowCounts = "unit" | "run_goods" | "left_out" | "presale";
/** One line of a run's Costs & invoices list. Rows not "manual" are read-only: they're recorded on a PO, a landed cost or the ledger. */
export type RunCostRow = {
  key: string;
  kind: RunRowKind;
  counts: RunRowCounts;
  layer: Layer;
  category: string;
  vendor: string;
  description: string;
  ref: string;
  /** The document numbers it can be recognised by (invoice no., PI or vendor ref, PO number), for the duplicate guard. */
  refs: string[];
  date: string | null;
  /** USD, cents: what was spent (ordered or invoiced); for a landed charge, its share on the run's lines that count. */
  cents: number;
  /** In its own currency: the line's or invoice's total (a per-unit cost × the units). */
  amount: number;
  currency: string;
  usdRate: number;
  poLineId: string | null;
  /** How it's spread over the run's variants. */
  spread: string;
  poId: string | null;
  costId: string | null;
  toolingId: string | null;
  lcId: string | null;
  owner: { type: "po" | "production_cost" | "tooling_item"; id: string } | null;
  paid: string;
  /** Bills and payments --- the bills it's on (a PO line: its PO's bills). */
  bills: RowBill[];
  flags: string[];
};

export type Attention = {
  key: string;
  costId: string | null;
  owner: RunCostRow["owner"];
  what: string;
  needs: string[];
  /** Bills and payments --- a payment's fee that may be this cost (entered by hand): "Same fee" / "Add it". */
  fee?: { paymentId: string; number: string; review: string };
};

export type Variance = {
  /** Per unit, cents: actual − planned, and its parts (they add up to total). */
  total: number;
  price: number;
  usage: number;
  fees: number;
  freight: number;
  volume: number;
  other: number;
  /** Set aside --- what a past run's units set aside add to each good unit (kept by the CM, given away). */
  setAside: number;
  /** The biggest part-price differences, for chips ("GCU2-RM2 +$0.72 a unit, +18%"). */
  parts: { itemId: string; sku: string; usd: number; pct: number | null; plannedUnit: number | null; actualUnit: number | null; why: string }[];
  why: Record<"price" | "usage" | "fees" | "freight" | "volume" | "other" | "setAside", string>;
};

export type RunResult = {
  run: ProductionRun;
  units: Map<string, RunUnits>;
  variants: string[];
  planned: Map<string, Stack>;
  actual: Map<string, Stack>;
  variance: Map<string, Variance>;
  rows: RunCostRow[];
  /**
   * Run totals, cents: planned (per unit × planned units), actual (charged to its good units), spent (every row that
   * counts, run goods too), run goods (in the spend, not the unit cost), left out (on its POs, not the run's).
   */
  totals: {
    plannedCents: number;
    actualCents: number;
    spentCents: number;
    runGoodsCents: number;
    leftOutCents: number;
    /** Pre-sales --- shipping to backers (backer_shipping): spent and in the pre-sale's result, never per unit. */
    presaleCents: number;
    goodUnits: number;
    /** Set aside --- a past run's units built that weren't HHL's to sell, and what they cost (in actualCents). */
    setAsideUnits: number;
    setAsideCents: number;
    byLayer: Record<Layer, { planned: number; actual: number }>;
  };
  reconcile: { label: string; cents: number; why: string }[];
  flags: string[];
  /** What still needs someone: costs with no category yet, no invoice, a foreign amount at a rate of 1. */
  attention: Attention[];
  /** The part quantities the plan prices at (price breaks). */
  partQty: Map<string, number>;
  /**
   * Pre-sales --- per variant, run totals in cents, of the freight layer, by kind (knownUnitCost()):
   * `landed` = the finished goods' own freight in and duty once landed (applied landed costs on its make PO line);
   * `recorded` = what could be them before then (the run's freight and duty entries not tied to a buy PO, draft
   * landed costs on its make PO line); `partsIn` = parts' freight and duty (landed costs on buy POs' lines, the run's
   * freight and duty entries on a buy PO). The rest of the layer (broker, payment fees, landed costs' fees) is on top.
   * All as split in the actual stack (by good units).
   */
  freightIn: Map<string, FreightIn>;
  /**
   * Pre-sales --- the run's freight and duty entries that could be the finished goods' (not on a buy PO), run totals in
   * cents by how they're split (units or value), so knownUnitCost() can spread them by units ordered before landing.
   */
  freightInRun: Record<"freight" | "duty", { units: number; value: number }>;
};

/** Pre-sales --- a variant's freight layer by kind, run totals in cents (RunResult.freightIn). */
export type FreightIn = {
  landed: { freight: number; duty: number };
  recorded: { freight: number; duty: number };
  /** Of `recorded`, draft landed costs on its make PO line (split by the landed cost itself). */
  drafts: { freight: number; duty: number };
  partsIn: number;
};

const NO_FREIGHT_IN: FreightIn = { landed: { freight: 0, duty: 0 }, recorded: { freight: 0, duty: 0 }, drafts: { freight: 0, duty: 0 }, partsIn: 0 };

/** Each part's planned quantity for the run (per unit × the run's planned units, every variant together). */
function runPartQty(cx: CostIndex, run: ProductionRun): Map<string, number> {
  const out = new Map<string, number>();
  const project = cx.projectById.get(run.project_id);
  for (const l of cx.linesByRun.get(run.id) ?? []) {
    const v = cx.ix.itemById.get(l.item_id);
    const bom = v ? bomFor(cx, v, l.bom_id) : undefined;
    if (!v || !bom) continue;
    const { leaves, builds } = explode(cx, bom, v, run.cm_vendor_id ?? bom.make_vendor_id ?? project?.cm_vendor_id ?? null);
    for (const leaf of leaves) out.set(leaf.itemId, (out.get(leaf.itemId) ?? 0) + leaf.qty * Number(l.qty));
    for (const b of builds) out.set(b.itemId, (out.get(b.itemId) ?? 0) + b.qty * Number(l.qty));
  }
  return out;
}

/** The plan to freeze when a run leaves planning: today's prices at the run's quantities, rates and duty. */
export function freezePlan(cx: CostIndex, runId: string): PlanBasis | null {
  const run = cx.runById.get(runId);
  if (!run) return null;
  const project = cx.projectById.get(run.project_id);
  const partQty = runPartQty(cx, run);
  const prices: PlanBasis["prices"] = {};
  const fees: PlanBasis["fees"] = {};
  const charges: NonNullable<PlanBasis["charges"]> = {};
  const duty: PlanBasis["duty"] = {};
  for (const l of cx.linesByRun.get(run.id) ?? []) {
    const v = cx.ix.itemById.get(l.item_id);
    const item = cx.itemById.get(l.item_id);
    const bom = v ? bomFor(cx, v, l.bom_id) : undefined;
    if (!v || !bom || !item) continue;
    const cm = run.cm_vendor_id ?? bom.make_vendor_id ?? project?.cm_vendor_id ?? null;
    const { leaves, builds, charges: charged } = explode(cx, bom, v, cm);
    for (const leaf of leaves)
      if (!prices[leaf.itemId]) {
        const p = plannedPrice(cx, leaf.itemId, partQty.get(leaf.itemId) ?? 1, null, leaf.rollup);
        prices[leaf.itemId] = { unit: p.unit, text: p.text, vendorId: p.vendorId };
      }
    for (const b of builds) {
      const vendor = b.itemId === v.id ? cm : b.vendorId;
      const qty = partQty.get(b.itemId) ?? Number(l.qty);
      // Charges --- a charged build's charges at the run's quantity, in place of a fee.
      const own = charged.filter((x) => x.build === b.itemId);
      if (own.length) {
        if (!charges[b.itemId]) charges[b.itemId] = chargesFor(cx, b.itemId, own, qty, null, vendor);
        continue;
      }
      const fee = plannedFee(cx, vendor, b.itemId, qty, null);
      if (fee && !fees[b.itemId]) fees[b.itemId] = fee;
    }
    const d = dutyFor(cx.d.dutyRates, item.hs_code, item.country_of_origin, day(run.target_ship) ?? cx.today);
    duty[l.item_id] = { pct: d.pct, text: d.layers.length ? d.layers.map((x) => `${x.layer} ${pctText(x.pct)}`).join(" + ") : "no rate" };
  }
  return {
    at: cx.today,
    prices,
    fees,
    charges,
    freight: (cx.freightByProject.get(run.project_id) ?? []).map((r) => ({ vendor_id: r.vendor_id, basis: r.basis, rate: Number(r.rate) })),
    duty,
    customs_basis: project?.customs_basis ?? "full",
  };
}

/**
 * A run's planned and actual cost per unit per variant, the variance split, and its Costs & invoices rows. Cached.
 */
export function runResult(cx: CostIndex, runId: string): RunResult | null {
  if (cx.cache.result.has(runId)) return cx.cache.result.get(runId) ?? null;
  const run = cx.runById.get(runId);
  if (!run) {
    cx.cache.result.set(runId, null);
    return null;
  }
  const project = cx.projectById.get(run.project_id);
  const basis = basisOf(run);
  const units = runUnits(cx, runId);
  const partQty = runPartQty(cx, run);
  const flags: string[] = [];
  const lineByItem = new Map((cx.linesByRun.get(runId) ?? []).map((l) => [l.item_id, l]));
  const pos = (cx.posByRun.get(runId) ?? []).filter((p) => OPEN_POS(p.status));
  const makeByItem = new Map<string, { mo: CostData["makeOrders"][number]; po: (typeof pos)[number] }[]>();
  for (const po of pos)
    if (po.kind === "make")
      for (const mo of cx.makeOrdersByPo.get(po.id) ?? []) makeByItem.set(mo.item_id, [...(makeByItem.get(mo.item_id) ?? []), { mo, po }]);
  // The run's variants: its plan's sell items (and sub-assemblies planned on their own), then anything else it built.
  const variants = [...new Set([...lineByItem.keys(), ...[...makeByItem.keys()].filter((id) => !isSubOfRun(cx, id, lineByItem))])].filter((id) =>
    cx.itemById.has(id),
  );

  const planned = new Map<string, Stack>();
  for (const id of variants) {
    const s = plannedStack(cx, id, { projectId: run.project_id, runId, bomId: lineByItem.get(id)?.bom_id, partQty, units: units.get(id)?.planned });
    if (s) planned.set(id, s);
  }

  // Actual, per build item, per good unit of it (memoised: a sub-assembly built in the run is used by its parents).
  // Packaging --- `packaging`: it goes in on a packaging line (a stocked packaging kit), so all its parts are packaging,
  // as explode() plans them; memoised apart, since a kit can also go in on a line that isn't.
  const memo = new Map<string, { exact: Record<Layer, number>; leaves: ActualLeaf[]; why: Record<Layer, Piece[]>; flags: string[] } | null>();
  const buildActual = (itemId: string, depth: number, packaging = false): ReturnType<typeof memo.get> => {
    const mk = packaging ? `${itemId}|packaging` : itemId;
    if (memo.has(mk)) return memo.get(mk);
    memo.set(mk, null);
    const builds = makeByItem.get(itemId);
    const item = cx.itemById.get(itemId);
    if (!builds?.length || !item || depth > 6) return null;
    const exact = zero();
    const why: Record<Layer, Piece[]> = { materials: [], assembly: [], packaging: [], freight: [], tooling: [], other: [] };
    const leaves: ActualLeaf[] = [];
    const myFlags: string[] = [];
    const u = units.get(itemId);
    // A past run reports no output: its units built are its plan's (and those set aside are in the cost of the rest).
    const reported = builds.reduce((n, b) => n + Number(b.mo.qty_output), 0);
    const built = reported > 0 ? reported : run.historical ? (u?.built ?? 0) : 0;
    const good = u?.good || built || builds.reduce((n, b) => n + Number(cx.poLineById.get(b.mo.po_line_id)?.qty ?? 0), 0) || 1;
    const perGood = built > 0 ? built / good : 1;
    const used = consumption(
      cx,
      builds.map((b) => b.mo.id),
    );
    // The snapshot's parts per unit (the first order's; several orders of one item weigh by quantity).
    const comps = new Map<string, MakeComponent & { weight: number }>();
    for (const b of builds) {
      const w = Number(cx.poLineById.get(b.mo.po_line_id)?.qty ?? 1);
      for (const c of (Array.isArray(b.mo.components) ? b.mo.components : []) as MakeComponent[]) {
        const k = `${c.item_id}|${c.supply}`;
        const had = comps.get(k);
        if (had) {
          had.per_unit = (had.per_unit * had.weight + Number(c.per_unit) * w) / (had.weight + w);
          had.weight += w;
        } else comps.set(k, { ...c, per_unit: Number(c.per_unit), weight: w });
      }
    }
    const packagingIds = packagingOf(cx, builds[0].mo.bom_id, item);
    // Charges --- make PO lines with charges: what the CM buys is in them (their "Parts the CM buys" row), not its own cost.
    const charged = builds.some((b) => (cx.chargesByLine.get(b.mo.po_line_id) ?? []).length > 0);
    let cmParts = 0;
    for (const c of comps.values()) {
      const ci = cx.itemById.get(c.item_id);
      const tracked = c.supply === "hhl_consigns" && c.kind !== "service";
      const use = used.get(c.item_id);
      const qtyPerGood = tracked && built > 0 && use ? (use.consumed + use.scrapped) / good : Number(c.per_unit) * perGood;
      const pack = packaging || packagingIds.has(c.item_id);
      const child = ci?.kind === "subassembly" ? buildActual(c.item_id, depth + 1, pack) : null;
      if (child) {
        for (const l of LAYERS) exact[l] += child.exact[l] * qtyPerGood;
        for (const leaf of child.leaves)
          leaves.push({
            ...leaf,
            key: `${c.item_id}/${leaf.key}`,
            qty: leaf.qty * qtyPerGood,
            usd: leaf.usd == null ? null : leaf.usd * qtyPerGood,
            plannedQty: leaf.plannedQty * Number(c.per_unit),
            via: [ci?.sku, leaf.via].filter(Boolean).join(" › "),
          });
        why.assembly.push(...child.why.assembly.map((p) => ({ ...p, usd: p.usd * qtyPerGood })));
        continue;
      }
      const subBom = ci && ci.kind === "subassembly" ? bomOf(cx.ix, cx.ix.itemById.get(c.item_id) as BomItem) : undefined;
      if (subBom && !subBom.phantom) {
        // A stocked sub-assembly not built in this run (from stock): its planned cost, layer by layer, at the quantity used.
        const sp = plannedStack(cx, c.item_id, { projectId: run.project_id, runId, partQty, basis, packaging: pack });
        if (sp) {
          for (const l of ["materials", "assembly", "packaging"] as const) exact[l] += sp.exact[l] * qtyPerGood;
          for (const line of sp.lines)
            leaves.push({
              ...line,
              key: `${c.item_id}/${line.key}`,
              qty: line.qty * qtyPerGood,
              usd: line.usd == null ? null : line.usd * qtyPerGood,
              via: [ci?.sku, line.via].filter(Boolean).join(" › "),
              plannedUnit: line.unit,
              plannedQty: line.qty * Number(c.per_unit),
              tracked: false,
            });
          why.assembly.push(...sp.why.assembly.map((p) => ({ ...p, usd: p.usd * qtyPerGood, detail: "planned: not built in this run" })));
          myFlags.push(`${ci?.sku} wasn't built in this run: its planned cost is used`);
          continue;
        }
      }
      const planned_ = plannedPrice(cx, c.item_id, partQty.get(c.item_id) ?? 1, basis, num(c.unit_cost));
      let unit = planned_.unit;
      let source: PriceSource = planned_.source;
      let text = planned_.text;
      if (tracked) {
        const paid = runPoPrice(cx, runId, c.item_id);
        if (paid.unit != null) {
          unit = paid.unit;
          source = "run_po";
          text = `Paid on ${paid.lines.map((l) => `${l.po} (${qtyText(l.qty)} @ ${usd(l.unit)})`).join(", ")}`;
        } else {
          text = `${paid.noRate || "Not bought for this run"}: planned price ${planned_.unit == null ? "—" : usd(planned_.unit)}`;
          if (paid.noRate) myFlags.push(paid.noRate);
        }
      }
      const layer = leafLayer({ kind: c.kind }, pack);
      const ext = unit == null ? null : unit * qtyPerGood;
      if (ext == null) myFlags.push(`${c.sku}: no price`);
      const inFee = byCm(c.supply) || c.kind === "service";
      if (inFee && c.kind !== "service" && ext != null) cmParts += ext;
      if (c.kind === "service" || (charged && inFee)) {
        leaves.push(toLeaf(cx, c, layer, qtyPerGood, unit, 0, text, source, inFee, tracked, planned_.unit, Number(c.per_unit)));
        continue;
      }
      if (ext != null) exact[layer] += ext;
      leaves.push(toLeaf(cx, c, layer, qtyPerGood, unit, ext, text, source, inFee, tracked, planned_.unit, Number(c.per_unit)));
    }
    // The CM's fee: the make PO lines' price per built unit, per good unit; assembly is it less the parts the CM buys.
    let feeTotal = 0;
    let feeQty = 0;
    const feeText: string[] = [];
    const feeLayers: Partial<Record<Layer, number>> = {}; // Charges --- the fee by its charges' layers (else assembly)
    for (const b of builds) {
      const line = cx.poLineById.get(b.mo.po_line_id);
      const rate = poRate(cx, b.po);
      if (!line || line.unit_price == null || rate == null) {
        myFlags.push(`${b.po.number} ${item.sku}: ${line?.unit_price == null ? "no price on the make PO line" : `in ${b.po.currency} with no USD rate`}`);
        continue;
      }
      const q = Number(b.mo.qty_output) > 0 ? Number(b.mo.qty_output) : Number(line.qty);
      const unit = mulRound(Number(line.unit_price), rate, 6);
      feeTotal += q * unit;
      feeQty += q;
      feeText.push(`${b.po.number} ${usd(unit)} × ${qtyText(q)}`);
      const rows = cx.chargesByLine.get(line.id) ?? [];
      const shares: Partial<Record<Layer, number>> = rows.length ? layerShares(rows, Number(line.qty)) : { assembly: 1 };
      for (const [layer, share] of Object.entries(shares) as [Layer, number][]) feeLayers[layer] = (feeLayers[layer] ?? 0) + q * unit * share;
    }
    if (feeQty > 0 && charged) {
      // Charges --- the fee per good unit in its charges' layers; the parts the CM buys are in it.
      const who = vendorName(cx, builds[0].po.vendor_id) || "CM";
      for (const [layer, total] of Object.entries(feeLayers) as [Layer, number][]) {
        const v = (total / feeQty) * perGood;
        exact[layer] += v;
        why[layer].push({
          label: `${item.sku}: ${who}'s charges (${LAYER_LABELS[layer]?.toLowerCase() ?? layer})${perGood !== 1 ? ` × ${qtyText(perGood)} built per good unit` : ""}`,
          usd: v,
          detail: feeText.join(", "),
        });
      }
    } else if (feeQty > 0) {
      const fee = (feeTotal / feeQty) * perGood;
      const assy = fee - cmParts;
      exact.assembly += assy;
      why.assembly.push({
        label: `${item.sku}: ${vendorName(cx, builds[0].po.vendor_id) || "CM"} fee ${usd(feeTotal / feeQty)}${perGood !== 1 ? ` × ${qtyText(perGood)} built per good unit` : ""}${cmParts ? ` less the parts it buys (${usd(cmParts)})` : ""}`,
        usd: assy,
        detail: feeText.join(", "),
      });
    }
    const out = { exact, leaves, why, flags: myFlags };
    memo.set(mk, out);
    return out;
  };

  // Pools: landed-cost charges, cost entries and tooling shares, split over the variants.
  const pools = new Map<string, Record<Layer, number>>(); // variant → cents per layer, run total
  const addPool = (variant: string, layer: Layer, cents: number) => {
    const p = pools.get(variant) ?? zero();
    p[layer] += cents;
    pools.set(variant, p);
  };
  const poolWhy = new Map<string, Record<Layer, Piece[]>>();
  const addWhy = (variant: string, layer: Layer, piece: Piece) => {
    const w = poolWhy.get(variant) ?? { materials: [], assembly: [], packaging: [], freight: [], tooling: [], other: [] };
    w[layer].push(piece);
    poolWhy.set(variant, w);
  };
  const freightIn = new Map<string, FreightIn>();
  const fi = (variant: string) => {
    const f = freightIn.get(variant) ?? structuredClone(NO_FREIGHT_IN);
    freightIn.set(variant, f);
    return f;
  };
  const freightInRun: RunResult["freightInRun"] = { freight: { units: 0, value: 0 }, duty: { units: 0, value: 0 } };
  const good = (id: string) => units.get(id)?.good ?? 0;
  const value = (id: string) => (planned.get(id)?.customs.value ?? 0) || (planned.get(id)?.total ?? 0);
  const spreadOver = (cents: number, ids: string[], byValue: boolean) => {
    const weights = ids.map((id) => good(id) * (byValue ? value(id) || 1 : 1));
    return splitBy(cents, weights);
  };
  // Which variants use an item (directly or through a sub-assembly) and how much, for spreading a part's charges.
  const perUnitOf = (v: string, itemId: string) => {
    const p = planned.get(v);
    const q = (p?.lines ?? []).filter((l) => l.itemId === itemId).reduce((n, l) => n + l.qty, 0);
    const asBuild = (p?.lines ?? []).length && itemId !== v ? subQty(cx, v, itemId, lineByItem.get(v)?.bom_id) : 0;
    return q + asBuild;
  };
  const usage = (itemId: string): Map<string, number> => {
    const out = new Map<string, number>();
    for (const v of variants) {
      const total = perUnitOf(v, itemId) * good(v);
      if (total > 0) out.set(v, total);
    }
    if (!out.size && variants.includes(itemId)) out.set(itemId, 1);
    return out;
  };
  // A part only variants the closed run never built use: bought for them and never used, so its charges stay with it
  // (spent, not charged to units, like the part itself) instead of falling on the units built.
  const unusedFor = (itemId: string): string[] =>
    variants.includes(itemId) || usage(itemId).size ? [] : variants.filter((v) => units.get(v)?.notBuilt && perUnitOf(v, itemId) > 0);

  // What the run builds with: its plans' parts and its make orders' snapshots. A line on one of its POs for anything
  // else (another project's parts on the same vendor's PO) isn't the run's: left out, unless marked run goods.
  const scope = new Set<string>(variants);
  for (const s of planned.values()) for (const l of s.lines) scope.add(l.itemId);
  for (const list of makeByItem.values())
    for (const { mo } of list) for (const c of (Array.isArray(mo.components) ? mo.components : []) as MakeComponent[]) scope.add(c.item_id);
  const countsOf = (line: CostData["poLines"][number] | undefined, po: CostData["pos"][number] | undefined): RunRowCounts =>
    !line ? "left_out" : line.run_goods ? "run_goods" : po?.kind === "make" || scope.has(line.item_id) || usage(line.item_id).size ? "unit" : "left_out";
  let runGoodsCents = 0;
  let leftOutCents = 0;

  const rows: RunCostRow[] = [];
  const poDate = (po: (typeof pos)[number]) => day(po.dated_on) ?? day(po.sent_on) ?? day(po.created_at);
  // PO lines (spent; the stack counts what was used, at these prices).
  for (const po of pos) {
    const rate = poRate(cx, po);
    for (const l of cx.linesByPo.get(po.id) ?? []) {
      const amount = l.unit_price == null ? 0 : mulRound(Number(l.qty), Number(l.unit_price), 2);
      const cents = rate == null ? 0 : Math.round(mulRound(amount, rate * 100, 6));
      const isFee = po.kind === "make";
      const item = cx.itemById.get(l.item_id);
      const counts = countsOf(l, po);
      const layer: Layer = isFee ? "assembly" : (layerOfPart(planned, l.item_id) ?? (item?.kind === "service" ? "assembly" : "other"));
      const users = usage(l.item_id);
      const unusedBy = unusedFor(l.item_id);
      const rowFlags: string[] = [];
      if (l.unit_price == null) rowFlags.push("No price on the line");
      if (rate == null) rowFlags.push(`${po.currency}: no USD rate yet (enter it on a landed cost's goods rates)`);
      if (counts === "left_out") {
        leftOutCents += cents;
        rowFlags.push("Not in this run's builds (another project's part?): left out of its cost. Mark it as run goods if this run paid for it");
      }
      if (counts === "run_goods") runGoodsCents += cents;
      rows.push({
        key: `po:${l.id}`,
        kind: isFee ? "make_fee" : "po_line",
        counts,
        layer,
        category: isFee
          ? "Build fee"
          : counts === "run_goods"
            ? "Run goods"
            : counts === "left_out"
              ? "Not in the builds"
              : (LAYER_LABELS[layer] ?? "Materials"),
        vendor: vendorName(cx, po.vendor_id),
        description: `${l.sku || ""} ${item?.shopify_variant_title || l.name}`.trim(),
        ref: po.number,
        refs: [po.number, po.vendor_ref ?? ""].filter(Boolean),
        date: poDate(po),
        cents,
        amount,
        currency: po.currency,
        usdRate: rate ?? 0,
        poLineId: l.id,
        spread:
          counts === "run_goods"
            ? "Run goods: in the run's spend, not in the cost per unit"
            : counts === "left_out"
              ? "Left out: not this run's"
              : isFee
                ? `Per built unit of ${l.sku}`
                : users.size
                  ? `At the quantity used: ${[...users.keys()].map((v) => cx.itemById.get(v)?.sku).join(", ")}`
                  : unusedBy.length
                    ? `At the quantity used: none (only ${unusedBy.map((v) => cx.itemById.get(v)?.sku).join(", ")}, which the run closed without building, use${unusedBy.length === 1 ? "s" : ""} it)`
                    : "At the quantity used",
        poId: po.id,
        costId: null,
        toolingId: null,
        lcId: null,
        owner: { type: "po", id: po.id },
        paid: "",
        bills: cx.billsByPo.get(po.id) ?? [],
        flags: rowFlags,
      });
    }
  }
  // Landed-cost charges on the run's POs: each charge's share on their lines, once, in freight & tariffs. The share on
  // run goods is spent but not in the unit cost; the share on lines left out isn't the run's.
  const seenLc = new Set<string>();
  for (const po of pos)
    for (const lcId of cx.lcsByPo.get(po.id) ?? []) {
      if (seenLc.has(lcId)) continue;
      seenLc.add(lcId);
      const lc = cx.lcById.get(lcId);
      if (!lc || lc.status === "void") continue;
      const alloc = allocationOf(cx, lcId);
      if (!alloc) continue;
      for (const ch of alloc.charges) {
        const charge = (cx.chargesByLc.get(lcId) ?? []).find((c) => c.id === ch.id);
        if (!charge) continue;
        const layer: Layer = charge.kind === "other" ? "other" : "freight";
        let onUnits = 0;
        let onGoods = 0;
        let out = 0;
        let unused = 0;
        const unusedSkus = new Set<string>();
        alloc.lines.forEach((bl, i) => {
          const c = ch.cents[i];
          if (!c || !pos.some((p) => p.id === bl.po_id)) return;
          const linePo = cx.poById.get(bl.po_id);
          const k = countsOf(cx.poLineById.get(bl.po_line_id), linePo);
          if (k === "left_out") {
            out += c;
            return;
          }
          if (k === "run_goods") {
            onGoods += c;
            return;
          }
          onUnits += c;
          const unusedBy = unusedFor(bl.item_id);
          if (unusedBy.length) {
            unused += c;
            for (const v of unusedBy) unusedSkus.add(cx.itemById.get(v)?.sku ?? "");
            return;
          }
          const users = linePo?.kind === "make" && variants.includes(bl.item_id) ? new Map([[bl.item_id, 1]]) : usage(bl.item_id);
          // Only variants with good units carry a charge: one on the make PO line of a variant the closed run never
          // built (split as ordered) falls on the run's good units.
          const targets = [...users.keys()].filter((v) => variants.includes(v) && good(v) > 0);
          const toVariants = targets.length ? targets : variants;
          const weights = targets.length ? toVariants.map((v) => users.get(v) ?? 0) : toVariants.map((v) => good(v));
          const parts = splitBy(c, weights);
          // Freight in and duty on the finished goods (its make PO line): landed once applied, a draft could be it;
          // on a buy PO's line: parts' freight. Fees and other kinds are on top.
          const kind = charge.kind === "freight" ? "freight" : charge.kind === "duty" || charge.kind === "tariff" ? "duty" : null;
          const own = linePo?.kind === "make" && variants.includes(bl.item_id);
          toVariants.forEach((v, n) => {
            addWhy(v, layer, { label: `${lc.number} ${charge.description || charge.kind}`, usd: parts[n] / 100, detail: `on ${bl.po_number} ${bl.sku}` });
            addPool(v, layer, parts[n]);
            if (!kind) return;
            if (!own) fi(v).partsIn += parts[n];
            else if (lc.status === "applied") fi(v).landed[kind] += parts[n];
            else {
              fi(v).recorded[kind] += parts[n];
              fi(v).drafts[kind] += parts[n];
            }
          });
        });
        runGoodsCents += onGoods;
        leftOutCents += out;
        rows.push({
          key: `lc:${ch.id}`,
          kind: "landed",
          counts: onUnits || (!onGoods && !out) ? "unit" : onGoods ? "run_goods" : "left_out",
          layer,
          category:
            charge.kind === "duty" || charge.kind === "tariff"
              ? "Tariff / duty"
              : charge.kind === "freight"
                ? "Freight"
                : charge.kind === "fee"
                  ? "Fee"
                  : "Other",
          vendor: lc.forwarder,
          description: charge.description || charge.kind,
          ref: `${lc.number}${lc.invoice_ref ? ` · ${lc.invoice_ref}` : ""}`,
          refs: [lc.number, lc.invoice_ref].filter(Boolean),
          date: day(lc.invoice_date),
          // What the run spent: its lines' share (run goods too); a charge only on lines left out shows what it was.
          cents: onUnits || onGoods ? onUnits + onGoods : out,
          amount: Number(charge.amount),
          currency: charge.currency,
          usdRate: Number(charge.usd_rate),
          poLineId: null,
          spread: `Landed cost's split (${charge.method}) on this run's PO lines`,
          poId: lc.po_id,
          costId: null,
          toolingId: null,
          lcId,
          owner: lc.po_id ? { type: "po", id: lc.po_id } : null,
          paid: "",
          bills: [],
          flags: [
            ...(lc.status === "draft" ? ["Draft landed cost: may still change"] : []),
            ...(onGoods && onUnits ? [`${usd(onGoods / 100)} of it is on run goods: in the run's spend, not the cost per unit`] : []),
            ...(out && (onUnits || onGoods) ? [`${usd(out / 100)} of it is on lines left out (not this run's): not counted`] : []),
            ...(unused
              ? [
                  `${usd(unused / 100)} of it is on parts only ${[...unusedSkus].join(", ")} use${unusedSkus.size === 1 ? "s" : ""}, which the run closed without building: not charged to units`,
                ]
              : []),
          ],
        });
      }
    }
  // The run's own cost entries.
  const totalGood = variants.reduce((n, v) => n + good(v), 0);
  let presaleCents = 0;
  for (const c of cx.costsByRun.get(runId) ?? []) {
    const layer = CATEGORY_LAYER[c.category as CostCategory] ?? "other";
    const cents = costCents(c, totalGood);
    // Pre-sales --- shipping to backers: in the run's spend and its pre-sale's result, never in a unit's cost.
    const backers = c.category === "backer_shipping";
    if (backers) presaleCents += cents;
    const byValue = splitsByValue(c);
    const parts = spreadOver(cents, variants, byValue);
    // Freight or duty not tied to a buy PO (none, or the make PO): it could be the finished goods' freight in or duty;
    // on a buy PO: parts' freight. Broker and payment fees are on top.
    const kind = c.category === "freight" ? "freight" : c.category === "tariff_duty" ? "duty" : null;
    const onBuy = !!c.po_id && cx.poById.get(c.po_id)?.kind !== "make";
    if (kind && !onBuy && !backers && variants.length) freightInRun[kind][byValue ? "value" : "units"] += cents;
    variants.forEach((v, i) => {
      if (backers) return;
      addPool(v, layer, parts[i]);
      if (kind && onBuy) fi(v).partsIn += parts[i];
      else if (kind) fi(v).recorded[kind] += parts[i];
      addWhy(v, layer, {
        label: `${COST_CATEGORY_LABELS[c.category as CostCategory]}: ${c.description || c.invoice_number || "cost"}`,
        usd: parts[i] / 100,
        detail: byValue ? "split by value" : "split by units",
      });
    });
    const dupe = duplicateAmong(rows, c, totalGood, false);
    const perUnit = c.basis === "per_unit";
    rows.push({
      key: `cost:${c.id}`,
      kind: "manual",
      counts: backers ? "presale" : "unit",
      layer,
      category: COST_CATEGORY_LABELS[c.category as CostCategory] ?? c.category,
      vendor: vendorName(cx, c.vendor_id),
      description: c.description,
      ref: c.invoice_number,
      refs: [c.invoice_number].filter(Boolean),
      date: day(c.invoice_date),
      cents,
      amount: perUnit ? mulRound(Number(c.amount), totalGood, 8) : Number(c.amount),
      currency: c.currency,
      usdRate: Number(c.usd_rate),
      poLineId: null,
      spread: backers
        ? "Shipping to backers: in the run's spend and its pre-sale's result, not in the cost per unit"
        : `${perUnit ? `${amountText(Number(c.amount), c.currency)} a unit × ${qtyText(totalGood)}; ` : ""}${byValue ? "split by value" : "split by units"}`,
      poId: c.po_id,
      costId: c.id,
      toolingId: null,
      lcId: null,
      owner: { type: "production_cost", id: c.id },
      bills: cx.billsByCost.get(c.id) ?? [],
      paid: paidText(cx, c),
      flags: [
        ...(c.category === "unknown" ? ["Category not known yet: in Other until it's set"] : []),
        ...(c.currency.toUpperCase() !== "USD" && Number(c.usd_rate) === 1 ? [`A ${c.currency} amount at a USD rate of 1: enter the real rate`] : []),
        ...(dupe ? [dupe] : []),
        ...(c.po_id && !pos.some((p) => p.id === c.po_id) ? ["Its PO isn't on this run"] : []),
      ],
    });
  }
  // Tooling: this run's share of each item, by its built (or planned) units.
  for (const t of cx.toolingByProject.get(run.project_id) ?? []) {
    if (t.retired) continue;
    const st = toolingStatus(cx, t);
    const share = st.runs.find((r) => r.runId === runId);
    const ids = variants.filter((v) => st.appliesTo.includes(v));
    if (share?.cents && ids.length) {
      const parts = spreadOver(share.cents, ids, false);
      ids.forEach((v, i) => {
        addPool(v, "tooling", parts[i]);
        addWhy(v, "tooling", { label: `${TOOLING_KIND_LABELS[t.kind]}: ${t.name}`, usd: parts[i] / 100, detail: st.text });
      });
    }
    if (t.paid_by_run_id === runId || share?.cents)
      rows.push({
        key: `tool:${t.id}`,
        kind: "tooling",
        counts: "unit",
        layer: "tooling",
        category: TOOLING_KIND_LABELS[t.kind],
        vendor: vendorName(cx, t.vendor_id),
        description: t.name,
        ref: t.invoice_number,
        refs: [t.invoice_number].filter(Boolean),
        date: day(t.invoice_date),
        cents: t.paid_by_run_id === runId ? st.cents : 0,
        amount: Number(t.amount),
        currency: t.currency,
        usdRate: Number(t.usd_rate),
        poLineId: null,
        spread: `${usd((share?.cents ?? 0) / 100)} on this run: ${st.text}`,
        poId: null,
        costId: null,
        toolingId: t.id,
        lcId: null,
        owner: { type: "tooling_item", id: t.id },
        bills: cx.billsByTooling.get(t.id) ?? [],
        paid: paidText(cx, t),
        flags: t.paid_by_run_id === runId ? [] : ["Paid by another run; this run carries its share"],
      });
  }

  // Actual stacks per variant: its build's parts and fee, plus its share of the pools, per good unit.
  const actual = new Map<string, Stack>();
  const variance = new Map<string, Variance>();
  for (const v of variants) {
    const u = units.get(v);
    // Never built (the run is closed): no good units to divide by, so no actual cost, and none of the pools.
    if (u?.notBuilt) continue;
    const item = cx.itemById.get(v) as CostItem;
    const p = planned.get(v);
    const g = good(v);
    const b = buildActual(v, 0);
    const exact = zero();
    const why: Record<Layer, Piece[]> = { materials: [], assembly: [], packaging: [], freight: [], tooling: [], other: [] };
    const vFlags: string[] = [];
    let lines: StackLine[] = [];
    if (b) {
      for (const l of LAYERS) exact[l] += b.exact[l];
      lines = b.leaves;
      for (const l of ["materials", "packaging"] as const)
        why[l] = b.leaves
          .filter((x) => x.layer === l && x.usd != null)
          .map((x) => ({ label: `${x.sku} × ${qtyText(x.qty)} @ ${usd(x.unit ?? 0)}`, usd: x.usd as number, detail: x.priceText, itemId: x.itemId }));
      why.assembly = [...b.why.assembly];
      vFlags.push(...b.flags);
    } else if (p && run.historical) {
      // A past run with no make order: the BOM's parts, each at what the run's POs paid for it (else the plan's price),
      // and the plan's fee; its costs and invoices come in with the pools below. Set aside --- per unit built, so each
      // good unit carries its share of those set aside (built ÷ good of them).
      const u_ = units.get(v);
      const f = u_ && u_.setAside > 0 && u_.good > 0 ? u_.built / u_.good : 1;
      for (const l of ["materials", "assembly", "packaging"] as const) exact[l] += p.exact[l] * f;
      const perBuilt = f === 1 ? p.lines : p.lines.map((l) => ({ ...l, qty: l.qty * f, usd: l.usd == null ? null : l.usd * f }));
      lines = perBuilt.map((l) => {
        if (l.charge || l.service || l.inFee || l.supply !== "hhl_consigns") return l;
        const paid = runPoPrice(cx, runId, l.itemId);
        if (paid.unit == null) return l;
        const ext = paid.unit * l.qty;
        exact[l.layer] += ext - (l.usd ?? 0);
        const text = `Paid on ${paid.lines.map((x) => `${x.po} (${qtyText(x.qty)} @ ${usd(x.unit)})`).join(", ")}`;
        return { ...l, unit: paid.unit, usd: ext, priceText: text, priceSource: "run_po" as PriceSource };
      });
      for (const l of ["materials", "packaging"] as const)
        why[l] = lines
          .filter((x) => x.layer === l && x.usd != null)
          .map((x) => ({ label: stackLabel(x), usd: x.usd as number, detail: x.priceText, itemId: x.itemId }));
      why.assembly = p.why.assembly.map((x) => (f === 1 ? x : { ...x, usd: x.usd * f, label: `${x.label} × ${qtyText(f)} built per good unit` }));
      vFlags.push("A past run: its BOM's parts at what its POs paid (else the plan's prices), and the plan's fee unless it has a make PO");
    } else if (p) {
      // Not ordered yet: the plan stands in for the build (flagged).
      for (const l of ["materials", "assembly", "packaging"] as const) exact[l] += p.exact[l];
      lines = p.lines;
      for (const l of ["materials", "assembly", "packaging"] as const) why[l] = [...p.why[l]];
      vFlags.push("No make order on this run yet: parts and fee are the plan's");
    }
    const pool = pools.get(v) ?? zero();
    const pw = poolWhy.get(v);
    for (const l of LAYERS) {
      if (g > 0) exact[l] += pool[l] / 100 / g;
      for (const piece of pw?.[l] ?? [])
        why[l].push({
          ...piece,
          usd: g > 0 ? piece.usd / g : 0,
          detail: `${usd(piece.usd)} for the run${piece.detail ? `, ${piece.detail}` : ""} ÷ ${qtyText(g)} good units`,
        });
    }
    if (u?.estimate) vFlags.push("Nothing built yet: divided by the planned units");
    const s = finish("actual", item, item.shopify_variant_title || item.name, exact, why, lines, {
      customs: p?.customs ?? {
        basis: project?.customs_basis ?? "full",
        value: 0,
        hts: item.hs_code,
        country: item.country_of_origin,
        pct: 0,
        layers: [],
        duty: 0,
        frozen: false,
      },
      missing: vFlags.filter((f) => /no price|no USD rate/.test(f)),
      flags: vFlags,
    });
    // Set aside --- what the units set aside add to each good unit, named in the flags and the variance.
    const aside = u && u.setAside > 0 && u.built > 0 ? { share: u.setAside / u.built, units: u.setAside, kept: u.good } : null;
    if (aside)
      s.flags.push(
        `${qtyText(aside.units)} of ${qtyText(u?.built ?? 0)} built set aside${run.set_aside_note ? ` (${run.set_aside_note})` : ""}: their cost is in the ${qtyText(aside.kept)} kept`,
      );
    actual.set(v, s);
    if (p)
      variance.set(
        v,
        varianceOf(
          cx,
          p,
          s,
          aside
            ? {
                share: aside.share,
                why: `the ${qtyText(aside.units)} set aside${run.set_aside_note ? ` (${run.set_aside_note})` : ""}, carried by the ${qtyText(aside.kept)} kept`,
              }
            : undefined,
        ),
      );
  }

  // Totals: planned per unit × planned units; actual charged to good units; spent on the rows.
  const byLayer = Object.fromEntries(LAYERS.map((l) => [l, { planned: 0, actual: 0 }])) as Record<Layer, { planned: number; actual: number }>;
  let plannedCents = 0;
  let actualCents = 0;
  let assumedCents = 0; // of actualCents: variants with nothing built yet, charged their plan
  let setAsideCents = 0; // of actualCents: Set aside --- what the units set aside cost, carried by the units kept
  let setAsideUnits = 0;
  for (const v of variants) {
    const p = planned.get(v);
    const a = actual.get(v);
    const pu = units.get(v)?.planned ?? 0;
    const g = good(v);
    if (p) {
      const t = roundParts(LAYERS.map((l) => (p.exact[l] * pu) as number));
      LAYERS.forEach((l, i) => {
        byLayer[l].planned += t[i];
      });
      plannedCents += t.reduce((x, y) => x + y, 0);
    }
    if (a) {
      const t = roundParts(LAYERS.map((l) => a.exact[l] * g));
      LAYERS.forEach((l, i) => {
        byLayer[l].actual += t[i];
      });
      actualCents += t.reduce((x, y) => x + y, 0);
      if (units.get(v)?.estimate) assumedCents += t.reduce((x, y) => x + y, 0);
      const u = units.get(v);
      if (u && u.setAside > 0 && u.built > 0) {
        // Everything but tooling, whose shares are per good unit (the units set aside take none).
        const perBuilt = t.reduce((x, y, i) => x + (LAYERS[i] === "tooling" ? 0 : y), 0);
        setAsideCents += Math.round((perBuilt * u.setAside) / u.built);
        setAsideUnits += u.setAside;
      }
    }
  }
  const spentCents = rows.reduce((n, r) => n + (r.counts === "left_out" ? 0 : r.cents), 0);
  // Spent less charged to units: a net number with causes on both sides, so its label says which way it goes and never
  // names one cause. Not "Difference" (the Result tab's difference is actual against plan, a different thing).
  const gapCents = spentCents - runGoodsCents - presaleCents - actualCents;
  const gapWhy = `Net: spent, less what's charged to good units${runGoodsCents ? " (run goods aren't in it)" : ""}${presaleCents ? " (nor shipping to backers)" : ""}. It goes up with parts bought and not used yet (left at the CM), units ordered but not built, and tooling another run carries; it goes down with units assumed from the plan (their planned parts and fee charged before they're spent), parts used from stock bought before this run, and tooling shares another run paid for`;
  const reconcile = [
    {
      label: "Spent",
      cents: spentCents,
      why: "PO lines as ordered, landed-cost charges on the run's POs, the run's own costs, tooling it paid for, run goods too (lines left out, another project's, aren't)",
    },
    ...(runGoodsCents
      ? [
          {
            label: "Run goods",
            cents: runGoodsCents,
            why: "PO lines marked run goods (cables for backers…) and their landed-cost share: the run paid for them, but they aren't in the cost per unit",
          },
        ]
      : []),
    ...(presaleCents
      ? [
          {
            label: "Shipping to backers",
            cents: presaleCents,
            why: "Postage, DDP, packing and freight for the pre-sale's boxes (category backer_shipping): in the pre-sale's result, never in the cost per unit",
          },
        ]
      : []),
    {
      label: "Charged to this run's good units",
      cents: actualCents,
      why: "What the actual cost per unit adds up to: parts used (not bought), fees for units built, the run's share of tooling",
    },
    ...(assumedCents
      ? [
          {
            label: "Of that, on assumed units",
            cents: assumedCents,
            why: "Variants with nothing built yet count their planned units until the CM reports output: their planned parts and fee per unit, plus their share of the run's charges (freight, duty, tooling), over those planned units",
          },
        ]
      : []),
    ...(setAsideCents
      ? [
          {
            label: `Of that, for the ${qtyText(setAsideUnits)} set aside`,
            cents: setAsideCents,
            why: `Units built that weren't Hand Held Legend's to sell${run.set_aside_note ? ` (${run.set_aside_note})` : ""}: what they cost, in the cost per unit of the units kept`,
          },
        ]
      : []),
    gapCents >= 0
      ? { label: "Not charged to units yet", cents: gapCents, why: gapWhy }
      : { label: "Charged to units beyond what this run spent", cents: -gapCents, why: gapWhy },
  ];
  if (!basis && run.status !== "planning") flags.push("This run's plan wasn't frozen; planned costs use today's prices");
  const notBuilt = variants.filter((v) => units.get(v)?.notBuilt).map((v) => cx.itemById.get(v)?.sku);
  if (notBuilt.length)
    flags.push(
      `Closed without building ${notBuilt.join(", ")}: no actual cost for ${notBuilt.length === 1 ? "it" : "them"}; the units built carry the run's charges`,
    );
  const result: RunResult = {
    run,
    units,
    variants,
    planned,
    actual,
    variance,
    rows: rows.sort((a, b) => LAYERS.indexOf(a.layer) - LAYERS.indexOf(b.layer) || (a.date ?? "").localeCompare(b.date ?? "")),
    totals: { plannedCents, actualCents, spentCents, runGoodsCents, leftOutCents, presaleCents, goodUnits: totalGood, setAsideUnits, setAsideCents, byLayer },
    reconcile,
    freightIn,
    freightInRun,
    flags,
    attention: attentionOf(cx, runId, rows),
    partQty,
  };
  cx.cache.result.set(runId, result);
  return result;
}

/**
 * A variant's landed cost per good unit as far as the run knows it: built, each layer actual where the run has an actual
 * cost for it, planned where it doesn't yet. Freight & tariffs by kind (Mitch, 2026-10-29), each on its own:
 * - freight in and duty on the finished goods: once landed (a landed cost of that kind applied on its make PO line),
 *   everything of that kind recorded (the landed cost and any other entry: a second container, parts-in freight on the
 *   make PO) over the good units; before, the larger of the plan's and what's recorded that could be it, spread by
 *   units ordered (between variants too: freightInRun);
 * - parts' freight: the larger of the plan's (other vendors' goods) and what's landed or entered on buy POs;
 * - broker, payment fees, landed costs' fees: always actual, on top.
 * Everything recorded counts once. Freight in and duty only fall on the units that came into the US (`opts.intlOf`:
 * international backers' units of each variant, which go from the CM to the DDP forwarder; `opts.intl`: of this one
 * only): spread over the rest, so the run's freight in comes out whole on them, and `inboundCents` (what an
 * international unit doesn't carry) is theirs. Nothing built: the plan.
 * `planned` lists the layers that came from the plan, `plannedParts` the parts still planned, `notes` say per kind
 * what's planned and what's actual.
 */
export function knownUnitCost(
  res: RunResult,
  variantId: string,
  opts: { intl?: number; intlOf?: (variantId: string) => number } = {},
): { cents: number; planned: Layer[]; plannedParts: string[]; notes: string[]; inboundCents: number; built: boolean } | null {
  const p = res.planned.get(variantId);
  const a = res.actual.get(variantId);
  const u = res.units.get(variantId);
  const built = (u?.built ?? 0) > 0;
  if (!built || !a)
    return p
      ? {
          cents: p.totalCents,
          planned: [...LAYERS],
          plannedParts: ["all of it (nothing built yet)"],
          notes: [],
          inboundCents: Math.round((p.inbound ?? 0) * 100),
          built: false,
        }
      : null;
  let cents = 0;
  let inboundCents = 0;
  const planned: Layer[] = [];
  const plannedParts: string[] = [];
  const notes: string[] = [];
  const good = u?.good ?? 0;
  const unit = (n: number) => `${usd(n)} a unit`;
  for (const l of LAYERS) {
    if (l === "freight" && p) {
      const f = res.freightIn?.get(variantId) ?? NO_FREIGHT_IN;
      const intlOf = opts.intlOf ?? ((v: string) => (v === variantId ? (opts.intl ?? 0) : 0));
      // Into the US: the good units less international backers' (never all of them), and before landing, the ordered.
      const intl = Math.max(0, intlOf(variantId));
      const usGood = good - intl > 0 ? good - intl : good;
      const usOrderedOf = (v: string) => {
        const uv = res.units.get(v);
        // A variant the closed run never built never came in.
        const o = uv?.notBuilt ? 0 : uv?.ordered || uv?.planned || uv?.good || 0;
        const i = Math.max(0, intlOf(v));
        return o - i > 0 ? o - i : o;
      };
      const usOrdered = usOrderedOf(variantId) || usGood;
      // Before landing, what's recorded is spread by units ordered into the US, the split between variants too (the
      // actual stack splits it by good units, which a variant barely built hardly has).
      const unitValue = (v: string) => (res.planned.get(v)?.customs.value ?? 0) || (res.planned.get(v)?.total ?? 0) || 1;
      const sumUnits = res.variants.reduce((n, v) => n + usOrderedOf(v), 0);
      const sumValue = res.variants.reduce((n, v) => n + usOrderedOf(v) * unitValue(v), 0);
      const run = res.freightInRun ?? { freight: { units: 0, value: 0 }, duty: { units: 0, value: 0 } };
      const estimate = (k: "freight" | "duty") =>
        f.drafts[k] +
        (sumUnits > 0 ? (run[k].units * usOrdered) / sumUnits : 0) +
        (sumValue > 0 ? (run[k].value * usOrdered * unitValue(variantId)) / sumValue : 0);
      const plannedDuty = p.customs.duty;
      const plannedIn = Math.max(0, (p.inbound ?? 0) - plannedDuty);
      const plannedParts_ = Math.max(0, p.exact.freight - (p.inbound ?? 0));
      const kindOf = (name: string, k: "freight" | "duty", plan: number) => {
        if (f.landed[k] > 0) {
          // Landed: actual, everything of that kind (the landed cost and anything else recorded, a second container,
          // parts-in freight on the make PO), over the units that came into the US.
          const v = (f.landed[k] + f.recorded[k]) / 100 / usGood;
          const also = f.recorded[k] > 0 ? ` with ${usd(f.recorded[k] / 100)} more recorded` : "";
          notes.push(`${name}: ${unit(v)}, actual (landed${also}, over the ${qtyText(usGood)} units that came into the US)`);
          return v;
        }
        const rec = estimate(k) / 100 / usOrdered;
        if (plan > rec) {
          plannedParts.push(name);
          if (plan > 0) notes.push(`${name}: the planned ${unit(plan)}, larger than the ${usd(rec)} recorded so far (not landed yet)`);
          return plan;
        }
        if (rec > 0) notes.push(`${name}: the ${unit(rec)} recorded so far, larger than the planned ${usd(plan)} (not landed yet)`);
        return rec;
      };
      const freightIn = kindOf("freight in", "freight", plannedIn);
      const duty = kindOf("duty", "duty", plannedDuty);
      // Parts' freight: the larger of the plan's and the actual.
      const partsActual = good > 0 ? f.partsIn / 100 / good : 0;
      const parts = Math.max(plannedParts_, partsActual);
      if (plannedParts_ > partsActual) {
        plannedParts.push("parts' freight");
        notes.push(`parts' freight: the planned ${unit(plannedParts_)}, larger than the ${usd(partsActual)} so far`);
      } else if (partsActual > 0 && plannedParts_ > 0) notes.push(`parts' freight: ${unit(partsActual)} actual, larger than the planned ${usd(plannedParts_)}`);
      // Everything else in the layer (broker, payment fees, landed costs' fees), actual on top.
      const known = good > 0 ? (f.landed.freight + f.landed.duty + f.recorded.freight + f.recorded.duty + f.partsIn) / 100 / good : 0;
      const fees = a.exact.freight - known;
      cents += Math.round((freightIn + duty + parts + fees) * 100);
      inboundCents += Math.round((freightIn + duty) * 100);
      if (plannedParts.includes("freight in") && plannedParts.includes("duty") && plannedParts_ >= partsActual && Math.abs(fees) < 0.000_05) planned.push(l);
      continue;
    }
    if (a.cents[l] !== 0 || !p || p.cents[l] === 0) cents += a.cents[l];
    else {
      cents += p.cents[l];
      planned.push(l);
      plannedParts.push(LAYER_LABELS[l].toLowerCase());
    }
  }
  return { cents, planned, plannedParts, notes, inboundCents, built: true };
}

/**
 * The run's costs that still need someone: a category not known yet (paid before its invoice came), no invoice attached
 * (a payment fee's receipt will do), a foreign amount still at a USD rate of 1. Only when attachments are known.
 */
function attentionOf(cx: CostIndex, runId: string, rows: RunCostRow[]): Attention[] {
  const out: Attention[] = [];
  for (const c of cx.costsByRun.get(runId) ?? []) {
    const needs: string[] = [];
    if (c.category === "unknown") needs.push("its category");
    if (cx.d.attachments) {
      const files = cx.filesByOwner.get(`production_cost:${c.id}`) ?? [];
      // Bills and payments --- a bill for it with its invoice attached counts too.
      const billed = (cx.billsByCost.get(c.id) ?? []).some((b) => (cx.filesByOwner.get(`bill:${b.id}`) ?? []).some((f) => f.kind !== "receipt"));
      // Pre-sales --- a shipment's own costs (postage totals from the backers app) have no invoice of their own.
      // An email it came by counts as its document (migration 076): some costs come by email with no invoice at all.
      if (
        !billed &&
        !c.presale_shipment_id &&
        !files.some((f) => f.kind === "invoice" || f.kind === "email" || (c.category === "payment_fee" && f.kind === "receipt"))
      )
        needs.push("its invoice");
    }
    if (c.currency.toUpperCase() !== "USD" && Number(c.usd_rate) === 1) needs.push(`the ${c.currency} rate (it's at 1)`);
    // Which card --- paid outside by card, but not which card or where (migration 073).
    if (c.paid_on && c.paid_method === "card" && (!c.paid_account_id || !c.paid_via)) needs.push("which card paid it, and where");
    if (!needs.length) continue;
    const row = rows.find((r) => r.costId === c.id);
    out.push({
      key: `cost:${c.id}`,
      costId: c.id,
      owner: { type: "production_cost", id: c.id },
      what: [
        vendorName(cx, c.vendor_id),
        c.invoice_number || c.paid_ref || c.description || COST_CATEGORY_LABELS[c.category as CostCategory],
        row ? usd(row.cents / 100) : "",
        c.paid_on ? `paid ${day(c.paid_on)}` : "",
      ]
        .filter(Boolean)
        .join(" · "),
      needs,
    });
  }
  // Bills and payments --- a paid fee that only matched a hand-entered one by amount: nothing was added, a person decides.
  const runCosts = new Set((cx.costsByRun.get(runId) ?? []).map((c) => c.id));
  for (const p of cx.d.payments ?? []) {
    if (!p.fee_review || !p.fee_matched_cost_id || !runCosts.has(p.fee_matched_cost_id)) continue;
    out.push({
      key: `fee:${p.id}`,
      costId: p.fee_matched_cost_id,
      owner: { type: "production_cost", id: p.fee_matched_cost_id },
      what: `${p.number}'s fee (${amountText(Number(p.fee), p.fee_currency || "USD")}), not added`,
      needs: ["a decision: is it the fee entered by hand?"],
      fee: { paymentId: p.id, number: p.number, review: p.fee_review },
    });
  }
  return out;
}

/** "$0.125" or "0.9 CNY": an amount in its own currency, every decimal it has. */
const amountText = (n: number, currency: string) =>
  currency.toUpperCase() === "USD"
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
    : `${n.toLocaleString("en-US", { maximumFractionDigits: 8 })} ${currency.toUpperCase()}`;

/** A cost entry's amount in USD (per unit for a per-unit one), to the cent: what the dialog and the MCP show. */
export const costUsd = (c: Pick<ProductionCost, "amount" | "currency" | "usd_rate">) =>
  c.currency.toUpperCase() === "USD" ? mulRound(Number(c.amount), 1, 2) : mulRound(Number(c.amount), Number(c.usd_rate), 2);

/**
 * A cost entry's USD cents for the run: amount × units (per unit) × rate, worked out in full and rounded once, so
 * $0.125 a unit × 8,000 is $1,000.00 (not 13¢ × 8,000) and $0.004 a unit is $32.00, not nothing.
 */
export function costCents(c: Pick<ProductionCost, "amount" | "currency" | "usd_rate" | "basis">, units: number): number {
  const rate = c.currency.toUpperCase() === "USD" ? 1 : Number(c.usd_rate);
  const total = c.basis === "per_unit" ? mulRound(Number(c.amount), units, 8) : Number(c.amount);
  return Math.round(mulRound(mulRound(total, rate, 8), 100, 0));
}

type DuplicateInput = Pick<ProductionCost, "po_id" | "category" | "amount" | "currency" | "usd_rate"> &
  Partial<Pick<ProductionCost, "basis" | "invoice_number">>;
const refKey = (s: string) => s.toLowerCase().replace(/[\s._/-]+/g, "");
const moneyOf = (r: RunCostRow) => (r.currency.toUpperCase() === "USD" ? usd(r.cents / 100) : `${amountText(r.amount, r.currency)}, ${usd(r.cents / 100)}`);

/**
 * The row a cost entry would repeat: the same money (USD to the cent, or the same amount in its own currency, so 2,100
 * CNY matches whatever rate each was entered at) or the same document (its invoice number is a PO's number or PI,
 * a landed cost's invoice, a tooling invoice), on the same PO (or none given). Read-only rows (`manual` false): any
 * kind of cost for a document, the same layer (or the make PO's fee) for money. Other entries (`manual` true): the same
 * layer for money, the same category for a document (one invoice can carry freight and duty). Lines left out aren't
 * the run's, so entering one of them as a cost counts it only once.
 */
function duplicateAmong(rows: RunCostRow[], c: DuplicateInput, units: number, manual: boolean, cx?: CostIndex): string | null {
  const cents = costCents({ ...c, basis: c.basis ?? "one_time" }, units);
  const currency = c.currency.toUpperCase();
  const amount = c.basis === "per_unit" ? mulRound(Number(c.amount), units, 8) : Number(c.amount);
  const layer = CATEGORY_LAYER[c.category as CostCategory] ?? "other";
  const ref = refKey(c.invoice_number ?? "");
  const sameMoney = (r: RunCostRow) =>
    (cents !== 0 && r.cents === cents) || (amount !== 0 && r.currency.toUpperCase() === currency && Math.abs(r.amount - amount) < 0.005);
  const sameRef = (r: RunCostRow) => ref !== "" && r.refs.some((x) => refKey(x) === ref);
  const samePo = (r: RunCostRow) => (manual ? r.poId === (c.po_id ?? null) : !c.po_id || r.poId === c.po_id);
  for (const r of rows) {
    if (r.counts === "left_out" || (r.kind === "manual") !== manual || !samePo(r)) continue;
    const category = cx ? cx.d.costs.find((x) => x.id === r.costId)?.category : undefined;
    const byRef = sameRef(r) && (!manual || category === c.category);
    const byMoney = sameMoney(r) && (r.layer === layer || (!manual && r.kind === "make_fee"));
    if (!byRef && !byMoney) continue;
    const what = `${r.ref || r.description || "another entry"}${r.ref && r.description && !manual ? ` ${r.description}` : ""} (${moneyOf(r)})`;
    if (manual) return `Looks like the same as ${what}${byRef ? `: the same invoice ${c.invoice_number}` : ""}`;
    return byRef ? `${c.invoice_number} is ${r.ref}'s reference: ${what} is counted already` : `Looks like ${what}, already counted`;
  }
  return null;
}

/** Whether a new cost entry would repeat something the run already counts (for the server's rule, the import and the dialog). */
export function duplicateCost(cx: CostIndex, c: DuplicateInput & Pick<ProductionCost, "run_id">, exceptId?: string): string | null {
  const r = runResult(cx, c.run_id);
  if (!r) return null;
  const others = r.rows.filter((x) => !x.costId || x.costId !== exceptId);
  return duplicateAmong(others, c, r.totals.goodUnits, false) ?? duplicateAmong(others, c, r.totals.goodUnits, true, cx);
}

function allocationOf(cx: CostIndex, lcId: string) {
  if (cx.cache.allocations.has(lcId)) return cx.cache.allocations.get(lcId) ?? null;
  const lc = cx.lcById.get(lcId);
  if (!lc) return null;
  const chosen = (cx.lcReceipts.get(lcId) ?? []).map((r) => r.receipt_id);
  const receipts = receiptsInScope(lc, chosen, cx.d.receipts as never[]) as CostData["receipts"][number][];
  const receiptIds = new Set(receipts.map((r) => r.id));
  const poIds = new Set([...receipts.map((r) => r.po_id), ...(lc.po_id ? [lc.po_id] : [])]);
  const { lines } = basisLines(lc, {
    receipts: receipts.map((r) => ({ ...r, received_at: String(r.received_at) })),
    receiptLines: cx.d.receiptLines.filter((rl) => receiptIds.has(rl.receipt_id)).map((rl) => ({ ...rl, qty_received: Number(rl.qty_received) })),
    poLines: cx.d.poLines.filter((l) => poIds.has(l.po_id)).map((l) => ({ ...l, qty: Number(l.qty), unit_price: num(l.unit_price) })),
    pos: [...poIds].flatMap((id) => (cx.poById.get(id) ? [cx.poById.get(id) as CostData["pos"][number]] : [])),
    items: cx.d.items.filter((i) => cx.d.poLines.some((l) => poIds.has(l.po_id) && l.item_id === i.id)) as never[],
  });
  const charges = (cx.chargesByLc.get(lcId) ?? []).map((c) => ({
    id: c.id,
    position: c.position,
    kind: c.kind,
    description: c.description,
    amount: Number(c.amount),
    currency: c.currency,
    usd_rate: Number(c.usd_rate),
    method: c.method,
  }));
  const alloc = allocate(lines, charges, (lc.goods_rates as Record<string, number>) ?? {});
  cx.cache.allocations.set(lcId, alloc);
  return alloc;
}

const layerOfPart = (planned: Map<string, Stack>, itemId: string): Layer | null => {
  for (const s of planned.values()) for (const l of s.lines) if (l.itemId === itemId) return l.layer;
  return null;
};

/** How many of a sub-assembly one unit of a variant takes (through its BOM), 0 if none. */
function subQty(cx: CostIndex, variantId: string, subId: string, bomId?: string | null): number {
  const v = cx.ix.itemById.get(variantId);
  const bom = v ? bomFor(cx, v, bomId) : undefined;
  if (!v || !bom) return 0;
  return explode(cx, bom, v, null)
    .builds.filter((b) => b.itemId === subId)
    .reduce((n, b) => n + b.qty, 0);
}

/** A sub-assembly the run builds for one of its planned variants (not a variant of its own). */
function isSubOfRun(cx: CostIndex, itemId: string, lines: Map<string, ProductionRunLine>): boolean {
  if (lines.has(itemId)) return false;
  for (const [v, l] of lines) if (subQty(cx, v, itemId, l.bom_id) > 0) return true;
  return false;
}

/** The parts a BOM revision marks as packaging (resolve()'s is_packaging), for an actual build. */
function packagingOf(cx: CostIndex, bomId: string | null, item: CostItem): Set<string> {
  const bom = bomId ? cx.ix.bomById.get(bomId) : undefined;
  const v = cx.ix.itemById.get(item.id);
  if (!bom || !v) return new Set();
  return new Set(
    resolve(cx.ix, bom, v)
      .lines.filter((l) => l.packaging)
      .map((l) => l.itemId),
  );
}

function toLeaf(
  cx: CostIndex,
  c: MakeComponent,
  layer: Layer,
  qty: number,
  unit: number | null,
  ext: number | null,
  text: string,
  source: PriceSource,
  inFee: boolean,
  tracked: boolean,
  plannedUnit: number | null,
  plannedQty: number,
): ActualLeaf {
  return {
    key: `${c.item_id}|${c.supply}`,
    layer,
    itemId: c.item_id,
    sku: c.sku,
    name: c.name,
    qty,
    unit,
    usd: ext,
    supply: c.supply,
    service: c.kind === "service",
    vendorId: cx.linksByItem.get(c.item_id)?.[0]?.vendor_id ?? null,
    priceText: text,
    priceSource: source,
    via: "",
    inFee,
    plannedUnit,
    plannedQty,
    tracked,
  };
}

// Variance -------------------------------------------------------------------------------------------------------

/**
 * Actual − planned per unit, split: price (paid more per part), usage (used more than the BOM, e.g. scrap and units
 * rejected), fees (assembly and packing), freight & tariffs, volume (one-time costs spread over fewer units) and
 * other. In cents that add up to the total exactly.
 */
export function varianceOf(_cx: CostIndex, planned: Stack, actualIn: Stack, aside?: { share: number; why: string }): Variance {
  const total = actualIn.totalCents - planned.totalCents;
  // Set aside --- the rest is split per unit built, so what the units set aside add is a part of its own.
  const actual = aside && aside.share > 0 ? perBuiltStack(actualIn, 1 - aside.share) : actualIn;
  const setAside = actualIn.totalCents - actual.totalCents;
  const d = (l: Layer) => actual.cents[l] - planned.cents[l];
  // Parts HHL buys: price at the actual quantity, the rest is usage.
  const pByKey = new Map(planned.lines.map((l) => [l.key, l]));
  let priceExact = 0;
  let _partsExactActual = 0;
  let _partsExactPlanned = 0;
  const parts: Variance["parts"] = [];
  const seen = new Set<string>();
  for (const a of actual.lines) {
    if (a.inFee || a.layer === "assembly") continue;
    const p = pByKey.get(a.key);
    const ap = a.unit ?? 0;
    const pp = p?.unit ?? (a as ActualLeaf).plannedUnit ?? ap;
    _partsExactActual += a.usd ?? 0;
    priceExact += (ap - pp) * a.qty;
    if (Math.abs((ap - pp) * a.qty) >= 0.005 && !seen.has(a.itemId)) {
      seen.add(a.itemId);
      parts.push({
        itemId: a.itemId,
        sku: a.sku,
        usd: (ap - pp) * a.qty,
        pct: pp ? ((ap - pp) / pp) * 100 : null,
        plannedUnit: pp,
        actualUnit: ap,
        why: `${a.sku}: ${usd(ap)} paid against ${usd(pp)} planned × ${qtyText(a.qty)} a unit`,
      });
    }
  }
  for (const p of planned.lines) if (!p.inFee && p.layer !== "assembly") _partsExactPlanned += p.usd ?? 0;
  const price = Math.round(mulRound(priceExact, 100, 6));
  const fees = d("assembly") + (d("packaging") - Math.round(mulRound(packagingParts(actual) - packagingParts(planned), 100, 6)));
  const freight = d("freight");
  const volume = d("tooling");
  const other = d("other");
  const usage = total - price - fees - freight - volume - other - setAside;
  const money = (c: number) => `${c >= 0 ? "+" : "−"}${usd(Math.abs(c) / 100)}`;
  return {
    total,
    price,
    usage,
    fees,
    freight,
    volume,
    other,
    setAside,
    parts: parts.sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd)),
    why: {
      price: `${money(price)}: parts paid at a different price than planned, at the quantity used${
        parts.length
          ? ` (${parts
              .slice(0, 3)
              .map((x) => `${x.sku} ${x.pct == null ? "" : `${x.pct >= 0 ? "+" : ""}${Math.round(x.pct)}%`}`)
              .join(", ")})`
          : ""
      }`,
      usage: `${money(usage)}: more or fewer parts used per good unit than the BOM (scrap, rejects, substitutions)`,
      fees: `${money(fees)}: the CM's fee, labour and packing against the plan`,
      freight: `${money(freight)}: freight, duty, broker and payment fees against the planned rates`,
      volume: `${money(volume)}: one-time costs spread over a different number of units`,
      other: `${money(other)}: costs under Other`,
      setAside: aside ? `${money(setAside)}: ${aside.why}` : "",
    },
  };
}

/**
 * Set aside --- a stack per unit built instead of per good unit (`kept` = good ÷ built): layers and lines scaled, but
 * tooling, whose shares are per good unit already (the units set aside take none).
 */
function perBuiltStack(s: Stack, kept: number): Stack {
  const cents = roundParts(LAYERS.map((l) => (s.cents[l] * (l === "tooling" ? 1 : kept)) / 100));
  const byLayer = Object.fromEntries(LAYERS.map((l, i) => [l, cents[i]])) as Record<Layer, number>;
  return {
    ...s,
    cents: byLayer,
    totalCents: cents.reduce((n, c) => n + c, 0),
    lines: s.lines.map((l) => ({ ...l, qty: l.qty * kept, usd: l.usd == null ? null : l.usd * kept })),
  };
}
const packagingParts = (s: Stack) => s.lines.filter((l) => l.layer === "packaging" && !l.inFee && !l.service).reduce((n, l) => n + (l.usd ?? 0), 0);

// Project level --------------------------------------------------------------------------------------------------

export type ProjectCosts = {
  projectId: string;
  /** The run whose units the plan prices at (the next open one, else the latest), if any. */
  volumeRun: ProductionRun | null;
  planned: Map<string, Stack>;
  /** The latest run with output (actual) per variant, and the latest closed one. */
  latest: { run: ProductionRun; result: RunResult } | null;
  latestClosed: { run: ProductionRun; result: RunResult } | null;
  variants: string[];
};

/** The project's variants' planned stacks (at the next run's quantities), and the latest run's actuals. Cached. */
export function projectCosts(cx: CostIndex, projectId: string, variantIds: readonly string[], whatIf?: WhatIf): ProjectCosts {
  const key = whatIf ? null : `${projectId}|${variantIds.join(",")}`;
  if (key && cx.cache.project.has(key)) return cx.cache.project.get(key) as ProjectCosts;
  const runs = cx.runsByProject.get(projectId) ?? [];
  const volumeRun = [...runs].reverse().find((r) => r.status !== "closed") ?? runs.at(-1) ?? null;
  const partQty = volumeRun ? runPartQty(cx, volumeRun) : new Map<string, number>();
  const planned = new Map<string, Stack>();
  for (const v of variantIds) {
    const s = plannedStack(cx, v, { projectId, partQty, whatIf, basis: null });
    if (s) planned.set(v, s);
  }
  const withOutput = [...runs].reverse().find((r) => [...runUnits(cx, r.id).values()].some((u) => u.built > 0));
  const closed = [...runs].reverse().find((r) => r.status === "closed");
  const lr = withOutput ? runResult(cx, withOutput.id) : null;
  const cr = closed ? runResult(cx, closed.id) : null;
  const out: ProjectCosts = {
    projectId,
    volumeRun,
    planned,
    latest: lr && withOutput ? { run: withOutput, result: lr } : null,
    latestClosed: cr && closed ? { run: closed, result: cr } : null,
    variants: [...variantIds],
  };
  if (key) cx.cache.project.set(key, out);
  return out;
}

export type LandedSource = { cost: number; source: "actual" | "planned"; run: ProductionRun | null; text: string };

/**
 * The landed cost Sell items' margin uses for a variant: actual from the latest closed run that built it, else the
 * planned stack when it's complete, else null (Shopify's cost then). In the variant's own project; a part, like a
 * sub-assembly reward, has none of its own and is costed in `inProject` (a pre-sale's run). A sell variant in no project
 * stays without one (its planned price, as before).
 */
export function landedCostOf(cx: CostIndex, variantId: string, inProject?: string | null): LandedSource | null {
  const item = cx.itemById.get(variantId);
  if (!item) return null;
  const projectId = projectOfVariant(cx, item) ?? (item.source === "local" ? inProject : null);
  if (!projectId) return null;
  const runs = [...(cx.runsByProject.get(projectId) ?? [])].reverse();
  for (const r of runs) {
    if (r.status !== "closed") continue;
    const res = runResult(cx, r.id);
    const a = res?.actual.get(variantId);
    const u = res?.units.get(variantId);
    if (a && u && u.built > 0 && a.complete)
      return {
        cost: a.total,
        source: "actual",
        run: r,
        text: `Actual landed cost from ${r.number} (closed): ${usd(a.total)} a unit over ${qtyText(u.good)} good units`,
      };
  }
  const variant = cx.ix.itemById.get(variantId);
  if (!variant || !bomOf(cx.ix, variant)) return null;
  const s = projectCosts(cx, projectId, [variantId]).planned.get(variantId) ?? plannedStack(cx, variantId, { projectId });
  if (!s?.complete) return null;
  return { cost: s.total, source: "planned", run: null, text: `Planned landed cost: ${usd(s.total)} a unit (no closed run yet)` };
}

/** The project a sell variant is in (item.project_id, which its product's link sets); a part has none of its own. */
export function projectOfVariant(_cx: CostIndex, item: Pick<Item, "project_id" | "shopify_product_id">): string | null {
  return item.project_id ?? null;
}

/**
 * A run's landed cost per unit for each variant it makes (Mitch, 2026-11-04: "at the end of the day we are interested in
 * the cost of a run, and the actual landed price, so that we can properly input the cost on our website"): what one good
 * unit cost once some are built (the actual stack: parts used, the maker's work, packaging, freight and duty, the run's
 * share of tooling), the plan until then (an estimate, never set in Shopify); Shopify's cost now, and whether Shopify's
 * cost can be set from it (built, every cost known, a Shopify variant). The run's Landed cost tab and
 * costs.set_shopify_from_run both read it.
 */
export function runLandedRows(cx: CostIndex, runId: string) {
  const res = runResult(cx, runId);
  if (!res) return [];
  // What the run makes to sell: its lines (a sub-assembly planned on its own too) and the sell items it built, never a
  // sub-assembly built for them, whose cost is inside theirs.
  const lines = new Set((cx.linesByRun.get(runId) ?? []).map((l) => l.item_id));
  return res.variants.flatMap((id) => {
    const u = res.units.get(id);
    const a = res.actual.get(id);
    const p = res.planned.get(id);
    const item = cx.itemById.get(id);
    if (!item || !(lines.has(id) || item.kind === "sell") || !(u?.planned || u?.ordered || u?.built)) return [];
    const built = !!a && (u?.built ?? 0) > 0;
    const stack = built ? a : p;
    const next = stack ? mulRound(stack.total, 1, 2) : null;
    const current = num(item.std_cost);
    const settable = built && !!a?.complete && item.source === "shopify" && next != null;
    return [
      {
        item_id: id,
        sku: item.sku,
        label: item.shopify_variant_title || item.name,
        units: u,
        stack,
        built,
        next,
        current,
        settable,
        same: next != null && current === next,
      },
    ];
  });
}
export type RunLandedRow = ReturnType<typeof runLandedRows>[number];

/** What the units a run makes to sell cost all together, in cents: each one's landed cost a unit × its units. */
export const runLandedCents = (rows: readonly RunLandedRow[]) => rows.reduce((n, r) => n + Math.round((r.stack?.total ?? 0) * (r.units?.good ?? 0) * 100), 0);

/** Each variant's Shopify cost push: its cost now, the landed cost to set and where it comes from. */
export function shopifyCostRows(cx: CostIndex, variantIds: readonly string[]) {
  return variantIds.flatMap((id) => {
    const item = cx.itemById.get(id);
    const src = landedCostOf(cx, id);
    if (item?.source !== "shopify" || !src) return [];
    const next = mulRound(src.cost, 1, 2);
    const current = num(item.std_cost);
    return [
      {
        item_id: id,
        sku: item.sku,
        label: item.shopify_variant_title || item.name,
        current,
        next,
        source: src.source,
        run: src.run?.number ?? null,
        text: src.text,
        same: current === next,
      },
    ];
  });
}

// Price flags -----------------------------------------------------------------------------------------------------

export type PriceFlag = { itemId: string; sku: string; pct: number; planned: number; actual: number; po: string; text: string; plannedText: string };

/**
 * A PO line priced more than PRICE_FLAG_PCT above its planned price ("GCU2-RM2 +18% on PO-1042"): the run's frozen
 * plan for a PO on a run, else the standard cost or the preferred quote at the line's quantity. USD lines only.
 */
export function priceFlag(
  cx: CostIndex,
  po: Pick<Po, "number" | "currency" | "production_run_id" | "kind">,
  line: Pick<PoLine, "item_id" | "sku" | "qty" | "unit_price">,
): PriceFlag | null {
  if (po.kind === "make" || line.unit_price == null || po.currency.toUpperCase() !== "USD") return null;
  const run = po.production_run_id ? cx.runById.get(po.production_run_id) : undefined;
  const basis = basisOf(run);
  // A draft's price that is the quote itself isn't a change; compare with the plan's (or the standard) price.
  const planned = plannedPrice(cx, line.item_id, Number(line.qty), basis, null);
  if (planned.unit == null || planned.unit <= 0) return null;
  const actual = Number(line.unit_price);
  const pct = ((actual - planned.unit) / planned.unit) * 100;
  if (pct <= PRICE_FLAG_PCT + 1e-9) return null;
  const sku = line.sku || cx.itemById.get(line.item_id)?.sku || "Part";
  return {
    itemId: line.item_id,
    sku,
    pct,
    planned: planned.unit,
    actual,
    po: po.number,
    text: `${sku} +${Math.round(pct)}% on ${po.number}`,
    plannedText: `${usd(actual)} against ${usd(planned.unit)} planned (${planned.text})`,
  };
}

/** Every flagged line on a run's POs (the ones in its cost per unit: not run goods, not lines left out). */
export function runPriceFlags(cx: CostIndex, runId: string): PriceFlag[] {
  const out: PriceFlag[] = [];
  const counted = new Set((runResult(cx, runId)?.rows ?? []).filter((r) => r.counts === "unit" && r.poLineId).map((r) => r.poLineId));
  for (const po of cx.posByRun.get(runId) ?? []) {
    if (!OPEN_POS(po.status)) continue;
    for (const l of cx.linesByPo.get(po.id) ?? []) {
      if (!counted.has(l.id)) continue;
      const f = priceFlag(cx, po, l);
      if (f) out.push(f);
    }
  }
  return out.sort((a, b) => b.pct - a.pct);
}

// Forecast ---------------------------------------------------------------------------------------------------------

/** The one-time costs of runs still to come (planning, ordering): tooling they pay for and their unpaid one-time entries. */
export function upcomingOneTime(cx: CostIndex): { runId: string; number: string; projectId: string; date: string; usd: number; what: string }[] {
  const out: { runId: string; number: string; projectId: string; date: string; usd: number; what: string }[] = [];
  for (const run of cx.d.runs) {
    if (run.status !== "planning" && run.status !== "ordering") continue;
    const date = day(run.target_ship) ?? cx.today;
    for (const t of cx.toolingByProject.get(run.project_id) ?? [])
      if (t.paid_by_run_id === run.id && !t.paid_on && !t.retired)
        out.push({
          runId: run.id,
          number: run.number,
          projectId: run.project_id,
          date: day(t.invoice_date) ?? date,
          usd: toolingUsd(t),
          what: `${TOOLING_KIND_LABELS[t.kind]}: ${t.name}`,
        });
    for (const c of cx.costsByRun.get(run.id) ?? [])
      if (!c.paid_on && c.basis === "one_time")
        out.push({
          runId: run.id,
          number: run.number,
          projectId: run.project_id,
          date: day(c.invoice_date) ?? date,
          usd: costUsd(c),
          what: `${COST_CATEGORY_LABELS[c.category as CostCategory]}: ${c.description || c.invoice_number}`,
        });
  }
  return out;
}

// Price history ----------------------------------------------------------------------------------------------------

/** A part's price history (USD points, oldest first) for the sparkline, with the planned price and the last paid. */
export function priceHistory(cx: CostIndex, itemId: string) {
  const points = (cx.pointsByItem.get(itemId) ?? []).filter((p) => p.currency.toUpperCase() === "USD");
  const planned = plannedPrice(cx, itemId, 1, null, null);
  const paid = [...points].reverse().find((p) => p.source === "po");
  return { points, planned, lastPaid: paid ?? null };
}

/** A run at MSRP: what its units would sell for at their Shopify prices, and what's left after their cost. */
export type RunAtMsrp = {
  lines: { itemId: string; units: number; price: number; cents: number }[];
  salesCents: number;
  costCents: number;
  /** "actual": what's charged to the good units; "planned": the plan's, while nothing's charged yet. */
  costFrom: "actual" | "planned";
  returnCents: number;
  /** Return as a share of sales; null without sales. */
  marginPct: number | null;
  /** Variants with units but no price (left out of sales, not of the cost). */
  unpriced: string[];
};

/**
 * Projected sales and return at MSRP (Mitch, 2026-11-03: "show projected sales and return based on MSRP for a run"):
 * each variant's good units (the plan's while nothing's built) at its Shopify price, less the units' cost (charged to
 * them, else the plan's). Before discounts, fees and pre-sale pricing.
 */
export function runAtMsrp(res: RunResult, priceOf: (itemId: string) => number | null | undefined): RunAtMsrp {
  const lines: RunAtMsrp["lines"] = [];
  const unpriced: string[] = [];
  let salesCents = 0;
  for (const itemId of res.variants) {
    const units = res.units.get(itemId)?.good ?? 0;
    if (units <= 0) continue;
    const price = priceOf(itemId);
    if (price == null || !(Number(price) > 0)) {
      unpriced.push(itemId);
      continue;
    }
    const cents = Math.round(units * Number(price) * 100);
    salesCents += cents;
    lines.push({ itemId, units, price: Number(price), cents });
  }
  const costFrom = res.totals.actualCents > 0 ? "actual" : "planned";
  const costCents = costFrom === "actual" ? res.totals.actualCents : res.totals.plannedCents;
  const returnCents = salesCents - costCents;
  return { lines, salesCents, costCents, costFrom, returnCents, marginPct: salesCents ? (returnCents / salesCents) * 100 : null, unpriced };
}
